/* Data-access layer.
   Leads are live (Worker → D1); KPIs, performance, lead-sources, and articles
   are still mock pending later phases. Everything the UI needs goes through here. */
import type { Article, ArticleStage, Channel, Kpi, Lead, LeadReferral, LeadSource, LeadSourceSlice, PerformancePoint } from './types';
import type { LeadStatus } from './status';
import { MOCK_KPIS, MOCK_LEAD_SOURCES, MOCK_PERFORMANCE } from './mock';
import { apiBlob, apiBlobUrl, apiFetch, apiSse } from './api';
import { processImage } from '@/lib/image';
import { daysSince } from '@/lib/leadTime';

function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/* ---- leads (live: /api/leads on D1) ---------------------------------- */

interface ApiLead {
  referral?: LeadReferral | null;
  uid: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  city: string | null;
  state: string | null;
  channel: string | null;
  score: number | null;
  source: string | null;
  summary: string | null;
  tags: string | null;
  transcript: string | null;
  callAudioLink: string | null;
  callDuration: number | null;
  chatUrl: string | null;
  receivedAt: string | null;
  referrer: string | null;
  value: number | null;
  status: LeadStatus | null;
  utm: { source: string | null; medium: string | null; campaign: string | null } | null;
}


function mapLead(a: ApiLead): Lead {
  return {
    uid: a.uid,
    name: a.name || 'Unknown',
    email: a.email,
    phone: a.phone,
    company: a.company,
    city: a.city,
    state: a.state,
    channel: (a.channel as Channel) ?? null,
    score: a.score ? Math.max(1, Math.min(5, Math.round(a.score))) : 0,
    source: (a.source as LeadSource) ?? null,
    value: a.value ?? null,
    status: a.status ?? 'unworked',
    days: daysSince(a.receivedAt),
    receivedAt: a.receivedAt,
    referrer: a.referrer,
    campaign: a.utm?.campaign ?? null,
    referral: a.referral ?? null,
    summary: a.summary,
    transcript: a.transcript,
    tags: a.tags,
    callAudioLink: a.callAudioLink,
    callDuration: a.callDuration,
    chatUrl: a.chatUrl,
  };
}

export async function getLeads(): Promise<Lead[]> {
  const { leads } = await apiFetch<{ leads: ApiLead[] }>('/api/leads');
  return leads.map(mapLead);
}

export async function setLeadStatus(uid: string, status: LeadStatus): Promise<void> {
  await apiFetch(`/api/leads/${encodeURIComponent(uid)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

export async function setLeadValue(uid: string, value: number | null): Promise<void> {
  await apiFetch(`/api/leads/${encodeURIComponent(uid)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ value }),
  });
}

/* ---- still mock (later phases) --------------------------------------- */

export function getKpis(): Promise<Kpi[]> {
  return delay(MOCK_KPIS);
}
export function getPerformance(): Promise<PerformancePoint[]> {
  return delay(MOCK_PERFORMANCE);
}
export function getLeadSources(): Promise<LeadSourceSlice[]> {
  return delay(MOCK_LEAD_SOURCES);
}

/* ---- contentIQ (live: /api/content on D1 + R2) ----------------------- */

export async function getArticles(): Promise<Article[]> {
  const { articles } = await apiFetch<{ articles: Article[] }>('/api/content');
  return articles;
}

export interface ContentInsights {
  configured: boolean;
  empty?: boolean;
  summary?: string;
  ideas?: { focus: string; kind: 'project-story' | 'knowledge'; location: string; service: string; rationale: string }[];
  cached?: boolean;
  generatedAt?: string;
  error?: string;
}
export function getContentInsights(refresh = false): Promise<ContentInsights> {
  return apiFetch(`/api/content/insights${refresh ? '?refresh=1' : ''}`);
}

/* One STORY on a live interview. Up to three share a session, a transcript and
   an interviewee; photos, documents and social posts each carry the story's id
   so they stay attached to the right one. Mirrors InterviewStory in
   worker/content.ts — the id is minted server-side and returned by
   createContentRequest so uploads can be tagged as they happen. */
export interface InterviewStory {
  id: string;
  title: string;
  jobType: string;
  city: string;
  description: string;
}
export const MAX_STORIES = 3;

export interface ContentRequestInput {
  title?: string; // project name (AI intake)
  jobType: string;
  city: string;
  description?: string;
  interviewee: string;
  intervieweeEmail?: string;
  source?: 'human' | 'ai';
  /** The topic this was started from. Its questions become the interview's. */
  topicId?: string;
  /** Live interviews only. Omit for a single-story request or an AI interview. */
  stories?: Omit<InterviewStory, 'id'>[];
}

export async function createContentRequest(input: ContentRequestInput): Promise<{ id: string; stories: InterviewStory[] }> {
  return apiFetch<{ id: string; stories: InterviewStory[] }>('/api/content', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/* ---- live interview scheduling (Google Calendar) --------------------- */
export interface InterviewSlots { slots: string[]; cm?: { name: string }; timezone?: string; noCm?: boolean; error?: string }
export function getInterviewSlots(articleId: string): Promise<InterviewSlots> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/slots`);
}
export function scheduleInterview(articleId: string, startIso: string, intervieweeEmail: string): Promise<{ ok: boolean; scheduledAt: string }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/schedule`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ startIso, intervieweeEmail }),
  });
}
/** Admin: the interview is happening now — mark it in progress and get the Roam link for the call. */
export function startInterviewNow(articleId: string, intervieweeEmail?: string): Promise<{ ok: boolean; roamUrl: string; dialIn: string; roamConfigured: boolean }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/start`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ intervieweeEmail: intervieweeEmail || '' }),
  });
}
export function skipInterviewSchedule(articleId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/skip`, { method: 'POST' });
}
export interface InterviewContact { name: string; email: string }
export function getInterviewContacts(): Promise<{ users: InterviewContact[] }> {
  return apiFetch('/api/content/client-users');
}
export interface CmAvailability { days: number[]; startHour: number; endHour: number; timezone: string }
export function getMyAvailability(): Promise<CmAvailability> { return apiFetch('/api/content/availability'); }
export function setMyAvailability(a: CmAvailability): Promise<{ ok: boolean }> {
  return apiFetch('/api/content/availability', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(a) });
}

export interface ArticleDetail {
  id: string;
  title: string;
  type: string;
  city: string;
  interviewee: string;
  expert: string;
  /** The attributed person, when one has been chosen. `expert` is the older
      free-text label and stays as the fallback. */
  expertId: string;
  stage: number;
  summary: string;
  body: string; // AI draft + transcript
  words: number;
  date: string;
  reviewQuote: string;
  reviewAuthor: string;
  source: string; // 'generated' | 'interview' | …
  /** Set when this article is a holding record for gap questions sent to the
   *  client — 'sent' while waiting, 'closed' once every answer is in. */
  gapStatus?: string;
  /** The blog brief that was drafted alongside those questions — often empty. */
  gapQuestions?: string[];
  /** The questions actually asked of the client, in order. */
  gapAsked?: string[];
  /** Admin only: each question with the client's answer (or why it was declined). */
  gapAnswers?: { question: string; answer: string; status: string; declinedReason: string }[];
  /** Admin only: the vault Q&A source those answers became, to emphasise in the generator. */
  gapSourceId?: string;
  /** The Expert Studio project kind behind this article: 'case' | 'knowledge'
   *  | 'blog' | ''. A Knowledge Session is blog-shaped, so the viewer offers
   *  the blog composer first for it. */
  studioKind?: string;
  /** What the client typed on a Knowledge Session: who it is for, what to cover. */
  studioAudience?: string;
  studioNotes?: string;
  genKind: string; // 'blog' | 'case_study' | ''
  genFormat: string; // 'astro_md' | 'html' | ''
  /** Admin only, live interviews: the Roam call to join, when it was booked or
      started, and the standing dial-in. */
  roamUrl?: string;
  scheduledAt?: string;
  roamDialIn?: string;
  dnnUrl: string; // set once delivered to the client's DNN site
  dnnPushedAt: string;
  /** The DNN article this piece is (pushed or linked); 0 when none. A push updates it. */
  dnnArticleId: number;
  /** SEO title and meta description, from the generator and edited since. Empty when none. */
  metaTitle: string;
  metaDescription: string;
}
export interface ArticlePhoto {
  id: string;
  filename: string;
  projectId: string;
  cdnUrl: string;
  /** Set when this photo is a gallery asset the case study shares. Empty on a
      legacy row, which still needs an admin to publish it before anything can
      use it. */
  assetId: string;
  /** Albums this same photo already appears in, by name. */
  albums: string[];
  /** Alt text the DNN image grid gives it. Empty falls back to its file name. */
  alt: string;
  /** "case-studies/<slug>/<name>.webp" once it has a named file; empty until it
      is named or first pushed to DNN. */
  publicPath: string;
  /** The line under it in the DNN photo grid. AI writes one; empty shows none. */
  caption: string;
  /** A video: plays rather than shows, never the main image. */
  isVideo: boolean;
}
export interface ArticleDocument {
  id: string;
  filename: string;
  contentType: string;
}
export interface InterviewProject { id: string; title: string }

export function getArticle(
  id: string,
): Promise<{
  isAdmin: boolean; article: ArticleDetail; projects: InterviewProject[];
  photos: ArticlePhoto[]; documents: ArticleDocument[];
  /** The photo chosen to lead the piece. Empty means nobody has chosen. */
  heroPhotoId: string;
  /** Photos on the interview this was written from that are not on it yet. */
  interviewPhotos: number;
}> {
  return apiFetch(`/api/content/${encodeURIComponent(id)}`);
}

/** Take a photo off a case study. Shared bytes survive if an album still holds them. */
export async function deleteContentPhoto(photoId: string): Promise<void> {
  await apiFetch(`/api/content/photos/${encodeURIComponent(photoId)}`, { method: 'DELETE' });
}

/** Name a case-study photo's file and/or set its alt text and caption. A rename
    keeps the old file live — a DNN page pushed before it still links that one. */
export async function setPhotoFile(photoId: string, input: { name?: string; alt?: string; caption?: string }): Promise<{ cdnUrl: string; alt: string; caption: string; publicPath: string }> {
  return apiFetch(`/api/content/photos/${encodeURIComponent(photoId)}/file`, {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
  });
}

/** Bring the interview's photos onto a case study, have AI write their captions,
    alt text and file names, file them into its folder and end its HTML with the grid. */
export async function organiseCaseStudyPhotos(articleId: string, input: { rewriteAlt?: boolean } = {}): Promise<{ attached: number; described: number; filed: number }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/photos/organise`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
  });
}

/** Which photo leads the piece. '' clears it. */
export async function setArticleHero(articleId: string, photoId: string): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(articleId)}/hero`, {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ photoId }),
  });
}

/** Put a photo the client already has on this case study. No bytes are copied. */
export async function addGalleryPhotoToArticle(articleId: string, assetId: string): Promise<{ id: string; assetId: string; cdnUrl: string }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/photos/from-gallery`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ assetId }),
  });
}
export async function setInterviewProjects(articleId: string, projects: InterviewProject[]): Promise<{ projects: InterviewProject[] }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/projects`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ projects }) });
}
export async function assignPhotoProject(photoId: string, projectId: string): Promise<void> {
  await apiFetch(`/api/content/photos/${encodeURIComponent(photoId)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ projectId }) });
}
export async function publishPhotoToCdn(photoId: string, name: string): Promise<{ url: string }> {
  return apiFetch(`/api/content/photos/${encodeURIComponent(photoId)}/publish`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name }) });
}

export async function deleteArticle(id: string): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function setArticleRejected(id: string, rejected: boolean): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ rejected }),
  });
}
export async function uploadContentDocument(articleId: string, file: File, opts: { projectId?: string } = {}): Promise<void> {
  const q = new URLSearchParams({ filename: file.name });
  if (opts.projectId) q.set('project', opts.projectId);
  await apiFetch(`/api/content/${encodeURIComponent(articleId)}/documents?${q.toString()}`, {
    method: 'POST',
    headers: { 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
}
export function getDocumentUrl(id: string): Promise<string> {
  return apiBlobUrl(`/api/content/documents/${encodeURIComponent(id)}`);
}
export async function attachTranscript(articleId: string, text: string): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(articleId)}/transcript`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }),
  });
}
export interface ReviewMatch { author: string; rating: number; date: string; content: string }
export async function searchClientReviews(q: string): Promise<{ reviews: ReviewMatch[] }> {
  return apiFetch(`/api/content/reviews/search?q=${encodeURIComponent(q)}`);
}
export async function attachReview(articleId: string, quote: string, author: string): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(articleId)}/review`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ quote, author }),
  });
}

/* ---- social posts (admin) -------------------------------------------- */

export type SocialPlatform = 'facebook' | 'instagram' | 'linkedin' | 'x';
export interface SocialPost {
  id: string; articleId: string; projectId: string; photoId: string; platform: SocialPlatform;
  tone: string; caption: string; body: string; status: 'draft' | 'approved';
  createdAt: string; approvedAt: string;
}
export async function getSocialPosts(articleId: string, projectId?: string): Promise<{ posts: SocialPost[] }> {
  const q = new URLSearchParams({ articleId });
  if (projectId) q.set('projectId', projectId);
  return apiFetch(`/api/social?${q.toString()}`);
}
export async function generateSocialPosts(input: { articleId: string; platforms: SocialPlatform[]; tone: string; photoId?: string; projectId?: string }): Promise<{ posts: SocialPost[] }> {
  return apiFetch('/api/social/generate', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function updateSocialPost(id: string, patch: { body?: string; caption?: string; status?: 'draft' | 'approved' }): Promise<void> {
  await apiFetch(`/api/social/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) });
}
export async function deleteSocialPost(id: string): Promise<void> {
  await apiFetch(`/api/social/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function getSocialFeedUrls(): Promise<{ base: string; byPlatform: Record<SocialPlatform, string> }> {
  return apiFetch('/api/social/feed-url');
}

/* ---- contentIQ topic suggestions ------------------------------------- */

export type TopicStatus = 'draft' | 'suggested' | 'used' | 'dismissed';
export interface Topic {
  id: string;
  title: string;
  projectType: string;
  city: string;
  keywords: string;
  products: string;
  services: string;
  notes: string;
  status: TopicStatus;
  source: 'admin' | 'ai';
  createdAt: string;
}
export interface TopicInput {
  title: string;
  projectType?: string;
  city?: string;
  keywords?: string;
  products?: string;
  services?: string;
  notes?: string;
}

export async function getTopics(): Promise<Topic[]> {
  const { topics } = await apiFetch<{ topics: Topic[] }>('/api/topics');
  return topics;
}
export async function getAllTopics(): Promise<Topic[]> {
  const { topics } = await apiFetch<{ topics: Topic[] }>('/api/topics/all');
  return topics;
}
export async function createTopic(input: TopicInput): Promise<{ id: string; near?: NearMatch[] }> {
  return apiFetch('/api/topics', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function suggestTopics(): Promise<{ generated: number; note?: string; topics: Topic[] }> {
  return apiFetch('/api/topics/suggest', { method: 'POST' });
}
export async function setTopicStatus(id: string, status: TopicStatus): Promise<void> {
  await apiFetch(`/api/topics/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ status }) });
}
export async function deleteTopic(id: string): Promise<void> {
  await apiFetch(`/api/topics/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function useTopic(id: string): Promise<void> {
  await apiFetch(`/api/topics/${encodeURIComponent(id)}/use`, { method: 'POST' });
}

/* ---- contentIQ Authenticity Vault (admin) ---------------------------- */

/* 'qa' is written by the gap flow when a client answers Triton's questions —
   first-hand client material like a transcript, kept distinguishable from one. */
export type VaultKind = 'transcript' | 'website_page' | 'sitemap' | 'profile' | 'article' | 'qa';

export interface VaultStructured {
  summary?: string;
  projects?: { name: string; summary: string; city: string }[];
  topics?: string[];
  entities?: { products?: string[]; brands?: string[]; services?: string[] };
  locations?: string[];
  people?: string[];
  quotable_lines?: string[];
}
export interface VaultSource {
  id: string;
  kind: VaultKind;
  title: string;
  origin: string;
  sourceUrl: string;
  tokens: number;
  status: string;
  analyzed: boolean;
  structured: VaultStructured | null;
  createdAt: string;
  updatedAt: string;
}
export interface VaultList {
  sources: VaultSource[];
  totalTokens: number;
  byKind: Record<string, number>;
}
export interface VaultCreateInput {
  method: 'paste' | 'crawl' | 'sitemap' | 'sitemap_paste' | 'gdrive';
  kind?: VaultKind;
  title?: string;
  text?: string;
  url?: string;
}

export async function getVault(): Promise<VaultList> {
  return apiFetch<VaultList>('/api/vault');
}
export async function getVaultSource(id: string): Promise<VaultSource & { rawText: string }> {
  return apiFetch(`/api/vault/${encodeURIComponent(id)}`);
}
export async function addVaultSource(input: VaultCreateInput): Promise<{ id: string; tokens: number; analyzed: boolean }> {
  return apiFetch('/api/vault', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function reanalyzeVaultSource(id: string): Promise<{ analyzed: boolean }> {
  return apiFetch(`/api/vault/${encodeURIComponent(id)}/reanalyze`, { method: 'POST' });
}
export async function buildVaultAnchors(id: string, offset = 0): Promise<{ nextOffset: number; total: number; processed: number }> {
  return apiFetch(`/api/vault/${encodeURIComponent(id)}/anchors?offset=${offset}`, { method: 'POST' });
}
export async function crawlSitemapPages(id: string, offset: number, limit = 12, overwrite = false): Promise<{ total: number; nextOffset: number; stored: number; updated: number; excluded: number; complete: boolean }> {
  return apiFetch(`/api/vault/${encodeURIComponent(id)}/crawl-pages`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ offset, limit, overwrite }),
  });
}
export async function uploadVaultFile(file: File, kind: VaultKind, title: string): Promise<{ id: string; analyzed: boolean }> {
  return apiFetch(`/api/vault/upload?kind=${encodeURIComponent(kind)}&title=${encodeURIComponent(title)}&filename=${encodeURIComponent(file.name)}`, {
    method: 'POST', headers: { 'content-type': file.type || 'application/octet-stream' }, body: file,
  });
}
export async function getCrawlFilter(): Promise<{ patterns: string[] }> {
  return apiFetch('/api/vault/crawl-filter');
}
export async function setCrawlFilter(patterns: string[]): Promise<void> {
  await apiFetch('/api/vault/crawl-filter', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ patterns }) });
}
export async function deleteVaultSource(id: string): Promise<void> {
  await apiFetch(`/api/vault/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
/** Clear every crawled website page (and optionally the sitemap) so the site can be recrawled. */
export function deleteVaultPages(withSitemap: boolean): Promise<{ ok: boolean; pages: number; sitemaps: number }> {
  return apiFetch(`/api/vault/kind/website_page${withSitemap ? '?sitemap=1' : ''}`, { method: 'DELETE' });
}

/* ---- contentIQ grounded generation (admin) --------------------------- */

export type OutputFormat = 'html' | 'astro_md';
export type GroundingMode = 'polished' | 'strict';

// 'vault': a transcript in the Vault with no interview behind it (id "vault:<source id>").
export interface GenStory { id: string; title: string; city: string; kind: 'ai' | 'human' | 'vault'; stage: number;
  /** The projects case studies were already written for (a name each; '' when none was picked). */
  written?: string[]; archived: boolean; studioKind?: string }
export interface GenerateContext {
  stories: GenStory[];
  pages: string[];
  hasSitemap: boolean;
  sourceCount: number;
  ready: boolean;
}
export interface HandoffFile { page: string; filename: string; content: string }

export async function getGenerateContext(): Promise<GenerateContext> {
  return apiFetch<GenerateContext>('/api/generate/context');
}
export type BlogWriter = 'classic' | 'evidence';
export interface EvidenceItem { text: string; source: string }
/** What the Vault establishes about THIS company on a topic, and what it could add. */
export interface EvidenceSheet {
  coverage: 'strong' | 'partial' | 'thin'; summary: string;
  claims: EvidenceItem[]; quotes: EvidenceItem[]; products: string[]; numbers: string[]; local: string[]; process: string[];
  /** Optional questions for the client — real jobs first, then facts, a local specific, a process step. */
  questions: string[];
}
export interface BlogAudit {
  scores: { effort: number; originality: number; skill: number; accuracy: number; overall: number };
  unsupported: string[]; cliches: string[]; missing: string[]; notes: string;
}
export interface CaseStudyResult {
  format: OutputFormat;
  output: string;
  title?: string;
  metaTitle?: string;
  metaDescription?: string;
  summary?: string;
  faqSchema?: string;
  changes?: string;
  writer?: BlogWriter;
  evidence?: EvidenceSheet;
  audit?: BlogAudit | null;
}
export interface BlogSettings { writer: BlogWriter; audit: boolean; autoWriteTopics: boolean; autoWriteInterviews: boolean; waitingDigestDaily: boolean }
export function getBlogSettings(): Promise<BlogSettings> { return apiFetch('/api/generate/blog-settings'); }
export function setBlogSettings(s: Partial<BlogSettings>): Promise<BlogSettings> {
  return apiFetch('/api/generate/blog-settings', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(s) });
}
export function auditBlogDraft(input: { output: string; topic: string; evidence?: EvidenceSheet | null; supportAnchor?: string; keywords?: string }): Promise<{ audit: BlogAudit }> {
  return apiFetch('/api/generate/blog/audit', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function reviseBlogDraft(input: { output: string; format: OutputFormat; notes: string; evidence?: EvidenceSheet | null }): Promise<{ output: string }> {
  return apiFetch('/api/generate/blog/revise', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function generateCaseStudy(input: { storyId?: string; projectName?: string; format: OutputFormat; mode: GroundingMode }): Promise<CaseStudyResult> {
  return apiFetch('/api/generate/case-study', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export interface StoryProject { name: string; summary: string; city: string }
export async function getStoryProjects(storyId: string): Promise<{ projects: StoryProject[] }> {
  return apiFetch('/api/generate/story-projects', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ storyId }) });
}
export interface BlogSupport { url: string; anchor: string; keywords: string }
export async function generateBlog(input: { topic: string; questions: string[]; format: OutputFormat; mode: GroundingMode; sourceIds?: string[]; notes?: string; writer?: BlogWriter; supportUrl?: string; supportAnchor?: string; keywords?: string; audit?: boolean }): Promise<CaseStudyResult> {
  return apiFetch('/api/generate/blog', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export type PromptFormat = 'auto' | 'markdown' | 'html' | 'plain';
export async function generatePrompt(input: { instruction: string; storyId?: string; format: PromptFormat; mode: GroundingMode }): Promise<CaseStudyResult> {
  return apiFetch('/api/generate/prompt', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export type RewriteFormat = 'auto' | 'markdown' | 'html' | 'plain';
export type RewriteStrength = 'light' | 'moderate' | 'heavy';
export async function rewriteContent(input: { content: string; format: RewriteFormat; mode: GroundingMode; strength: RewriteStrength; instruction?: string; factCheck?: boolean }): Promise<CaseStudyResult> {
  return apiFetch('/api/generate/rewrite', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function publishGenerated(input: { title?: string; summary?: string; body: string; label?: string; stage?: 1 | 3; sourceStoryId?: string; kind?: 'blog' | 'case_study'; format?: string; publishDate?: string; heroUrl?: string; heroAlt?: string; siteUrl?: string; metaTitle?: string; metaDescription?: string; sourceProject?: string; projectCount?: number }): Promise<{ id: string }> {
  return apiFetch('/api/content/publish', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
/** A vault-backed suggestion, with the coverage verdict already proven. */
export interface SuggestedTopic { topic: string; questions: string[]; gaps: string[]; coverage: TopicCoverage | null; nearTo?: NearMatch[] }
export async function suggestBlogTopics(): Promise<{
  topic: string; questions: string[]; gaps: string[];
  coverage: TopicCoverage | null; alternatives: SuggestedTopic[]; note?: string;
}> {
  return apiFetch('/api/generate/blog-topics', { method: 'POST' });
}

// Interviews/stories (with a transcript) + manually-added Vault transcripts, to
// mine deep-dive blog topics from. kind: 'interview' (a story) | 'vault' (added
// straight into the Vault).
export interface BlogSource { id: string; title: string; city: string; createdAt: string; kind: 'interview' | 'vault' }
export async function getBlogSources(): Promise<{ items: BlogSource[] }> {
  return apiFetch('/api/generate/blog-sources');
}
export interface InterviewTopicCandidate { title: string; angle: string; questions: string[]; gaps: string[]; keywords?: string[]; nearTo?: NearMatch[] }
export async function suggestTopicsFromInterviews(sourceIds: string[]): Promise<{ candidates: InterviewTopicCandidate[]; note?: string }> {
  return apiFetch('/api/generate/blog-topics/from-sources', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ sourceIds }) });
}

// Sitemap pages (pillar pages) + supporting topic-cluster suggestions.
export interface SitemapPage { url: string; anchor: string }
/** An SEO focus page as the picker sees it: ranked by position, flagged when the sitemap lacks it. */
export interface FocusPagePick extends SeoFocusPage { inSitemap: boolean }
export async function getSitemapPages(): Promise<{ pages: SitemapPage[]; focus: FocusPagePick[] }> {
  return apiFetch('/api/generate/sitemap-pages');
}
/** Queue the chosen supporting topics for one page as a batch; the SEO team is emailed. */
export async function sendTopicBatch(input: { url: string; anchor: string; topics: { topic: string; questions: string[]; keywords: string[] }[] }): Promise<{ batchId: string; ids: string[]; near?: NearMatch[][] }> {
  return apiFetch('/api/generate/blog-queue/batch', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function suggestTopicsForPage(url: string): Promise<{ candidates: InterviewTopicCandidate[]; note?: string; page?: { url: string; anchor: string; keywords: string; crawled: boolean } }> {
  return apiFetch('/api/generate/blog-topics/from-page', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ url }) });
}

// Does the Vault have enough grounded material to write a given topic?
/** A title this client already has that is close to the one in hand. Advisory:
 *  it is shown, never enforced. `where` says which list it came from. */
export interface NearMatch { id?: string; title: string; where: 'covered' | 'queue' | 'brief' | 'article'; detail?: string; url?: string; score: number }
export async function similarTopics(q: string): Promise<{ near: NearMatch[] }> {
  return apiFetch(`/api/generate/blog-topics/similar?q=${encodeURIComponent(q)}`);
}
export interface TopicCoverage {
  verdict: 'strong' | 'partial' | 'thin'; score: number; supporting: { title: string; why: string }[]; gaps: string[];
  /** The sections the post would have, in reading order — returned even on a thin
   *  verdict, because an unsupported heading is the brief for what to go and ask. */
  subtopics: { heading: string; supported: boolean; why: string }[];
  /** The supported headings only — what the composer loads as its outline. */
  questions: string[];
  note?: string;
  /** Close existing titles, from the assess call. Absent on older responses. */
  near?: NearMatch[];
}
export async function assessTopicCoverage(topic: string): Promise<TopicCoverage> {
  return apiFetch('/api/generate/blog-topics/assess', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ topic }) });
}

export type BlogTopicOrigin = 'suggest' | 'own' | 'interview' | 'ai-idea' | 'page';
export interface BlogQueueItem {
  id: string; topic: string; questions: string[]; status: 'pending' | 'approved' | 'rejected'; notes: string; createdAt: string; sourceIds: string[]; origin: string;
  /** The sitemap page this topic was requested to support, and the batch it was sent in. */
  supportUrl: string; supportAnchor: string; batchId: string; keywords: string; requestedBy: string;
  /** Pending only: the client's approver, the same person the request email went to. */
  waitingOn: { name: string; role: string } | null;
  /** Approved/rejected only: when. */
  decidedAt: string | null;
  /** The auto-write job behind this topic, when the switch was on at approval. */
  autoWrite?: { status: 'queued' | 'writing' | 'done' | 'failed' | string; articleId: string | null; error: string | null } | null;
}
/** A brief as the Topic inbox lists it: already filtered against covered titles server-side. */
export interface InboxBrief { id: string; title: string; city: string; keywords: string; status: TopicStatus; source: 'admin' | 'ai'; near?: NearMatch[] }
export function getTopicInbox(): Promise<{ briefs: InboxBrief[]; queue: (BlogQueueItem & { near?: NearMatch[] })[]; hidden: number; coveredCount: number }> {
  return apiFetch('/api/generate/blog-topics/inbox');
}
export async function getBlogQueue(): Promise<{ items: BlogQueueItem[] }> {
  return apiFetch('/api/generate/blog-queue');
}
/** `notify` emails the Optimization Manager that a topic is waiting — set it for
 *  a deliberate hand-off, leave it off when quietly queueing mined topics. */
export async function saveBlogTopic(input: { topic: string; questions: string[]; sourceIds?: string[]; origin?: BlogTopicOrigin; notify?: boolean }): Promise<{ id: string; near?: NearMatch[] }> {
  return apiFetch('/api/generate/blog-queue', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export async function updateBlogTopic(id: string, patch: { topic?: string; questions?: string[]; status?: 'pending' | 'approved' | 'rejected'; notes?: string }): Promise<void> {
  await apiFetch(`/api/generate/blog-queue/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) });
}
export async function deleteBlogTopic(id: string): Promise<void> {
  await apiFetch(`/api/generate/blog-queue/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function regenerateBlogPiece(input: { topic: string; questions: string[]; target: 'topic' | 'questions' | 'question'; index?: number; guidance?: string }): Promise<{ topic?: string; questions?: string[]; question?: string }> {
  return apiFetch('/api/generate/blog-regenerate', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}

export interface CoveredTopic { id: string; topic: string; subtopics: string | null; url: string | null; source: string; created_at: string }
export async function getCoveredTopics(): Promise<{ covered: CoveredTopic[] }> {
  return apiFetch('/api/generate/covered');
}
export async function importCoveredTopics(text: string): Promise<{ added: number }> {
  return apiFetch('/api/generate/covered', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) });
}
export async function deleteCoveredTopic(id: string): Promise<void> {
  await apiFetch(`/api/generate/covered/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function generateWebHandoff(input: { mode: GroundingMode; page?: string; offset?: number }): Promise<{ files: HandoffFile[]; note?: string; next?: number | null; total?: number }> {
  return apiFetch('/api/generate/web-handoff', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}

/** Site pages: the finished page file for one sitemap route, in the client repo's
 *  own layout. `layoutSource` says which rung of the skeleton ladder was used —
 *  a 'default' means we couldn't see the client's real layout. */
export const SITE_PAGE_TYPES = ['home', 'service', 'location', 'about', 'contact', 'other'] as const;
export type SitePageType = (typeof SITE_PAGE_TYPES)[number];
export type LayoutSource = 'pasted' | 'repo' | 'saved' | 'default';

/** How the layout file's TEXT is treated. `layout_only` (default) strips its
    prose before the model sees it and keeps the page's own crawled copy out of
    the sources — the page is written fresh from the Vault. `rewrite` keeps the
    file's facts and refreshes the wording. */
export type LayoutUse = 'layout_only' | 'rewrite';
export async function generateSitePage(input: {
  page: string; mode: GroundingMode; layout?: string; layoutPath?: string; pageType?: SitePageType; notes?: string; layoutUse?: LayoutUse; expertId?: string;
}): Promise<{ files: HandoffFile[]; note?: string; layoutSource: LayoutSource; repoPath?: string; pushPath: string; pageType: SitePageType; layoutUse: LayoutUse; lint: string; repairs: string[] }> {
  return apiFetch('/api/generate/site-page', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
/** Auto-fix the front-matter of an edited site page and report what still
    blocks a push. `text` is the repaired file — adopt it. */
export async function checkSitePage(body: string): Promise<{ text: string; repairs: string[]; lint: string }> {
  return apiFetch('/api/generate/site-page/check', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ body }) });
}
export async function getSitePageLayout(page: string, type?: SitePageType): Promise<{
  route: string; type: SitePageType; skeleton: string; source: LayoutSource; repoPath: string;
}> {
  const q = new URLSearchParams({ page });
  if (type) q.set('type', type);
  return apiFetch(`/api/generate/site-page/layout?${q.toString()}`);
}
/* Sitemap ↔ repo reconciliation. Slugs drift once, when the site is scaffolded
   and the build simplifies them; correcting the stored sitemap then fixes every
   generator at once, because internal links are built from the vault. */
export async function getSitePageRepoFiles(): Promise<{ files: string[]; note?: string }> {
  return apiFetch('/api/generate/site-page/repo-files');
}
export async function getSitePageRepoFile(path: string): Promise<{ path: string; content: string }> {
  return apiFetch(`/api/generate/site-page/file?path=${encodeURIComponent(path)}`);
}
export async function getSitePageTemplates(): Promise<{ templates: Record<SitePageType, string>; defaultLayout: string }> {
  return apiFetch('/api/generate/site-page/templates');
}
export async function setSitePageTemplate(type: SitePageType, body: string): Promise<void> {
  await apiFetch('/api/generate/site-page/templates', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type, body }) });
}
/* ---- AI hero images (contentIQ) --------------------------------------- */
export interface HeroJob {
  id: string; status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'nsfw' | 'canceled';
  error: string; ready: boolean; cdnUrl: string; prompt: string; attempt: number; createdAt?: string;
}
export function getHeroUsage(): Promise<{ used: number; cap: number; configured: boolean }> {
  return apiFetch('/api/generate/hero-image/usage');
}
export function getHeroLocaleBrief(): Promise<{ brief: string; configured: boolean }> {
  return apiFetch('/api/generate/hero-image/locale-brief');
}
export async function setHeroLocaleBrief(brief: string): Promise<void> {
  await apiFetch('/api/generate/hero-image/locale-brief', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ brief }) });
}
/** What would be sent, so it can be reviewed and edited before spending a credit. */
/** `note` says what the request could NOT honour — empty when it was met in full. */
export function buildHeroPrompt(input: { topic: string; extra?: string; article?: string; allowPeople?: boolean }): Promise<{ prompt: string; note?: string }> {
  return apiFetch('/api/generate/hero-image/prompt', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function startHeroImage(input: { prompt: string; articleId?: string }): Promise<{ jobId: string; status: string; attempt: number; used: number; cap: number }> {
  return apiFetch('/api/generate/hero-image', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function getHeroJob(id: string): Promise<HeroJob> {
  return apiFetch(`/api/generate/hero-image/${encodeURIComponent(id)}`);
}
/** Everything this client has generated, for reuse without spending again. */
export interface HeroLibraryImage { id: string; prompt: string; cdnUrl: string; ready: boolean; articleId: string; createdAt: string }
export function getHeroLibrary(): Promise<{ images: HeroLibraryImage[] }> {
  return apiFetch('/api/generate/hero-image/library');
}
export function getHeroJobsForArticle(articleId: string): Promise<{ jobs: HeroJob[] }> {
  return apiFetch(`/api/generate/hero-image/article/${encodeURIComponent(articleId)}`);
}
/** Auth-gated, same-origin — which is what lets the canvas optimize it. */
export function heroImageUrl(id: string): Promise<string> {
  return apiBlobUrl(`/api/generate/hero-image/${encodeURIComponent(id)}/raw`);
}
export async function publishHeroImage(
  id: string, file: Blob, slug: string,
  /** Set when accepting from a saved draft — links the image to that article. */
  link?: { articleId?: string; alt?: string; prefix?: 'hero' | 'image' },
): Promise<{ url: string }> {
  const q = new URLSearchParams({ slug });
  if (link?.articleId) { q.set('articleId', link.articleId); q.set('alt', link.alt || ''); }
  if (link?.prefix) q.set('prefix', link.prefix);
  return apiFetch(`/api/generate/hero-image/${encodeURIComponent(id)}/publish?${q.toString()}`, {
    method: 'POST', headers: { 'content-type': file.type || 'image/webp' }, body: file,
  });
}
export async function deleteHeroImage(id: string): Promise<void> {
  await apiFetch(`/api/generate/hero-image/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface SetupStatus {
  websitePages: number; sitemaps: number; transcripts: number; anchorsBuilt: boolean;
  coveredTopics: number; templates: Record<string, boolean>; hasHouseStyle: boolean; assetFolder: string; folderOverride: string;
  contentPlatform: ContentPlatform;
}
export async function getSetupStatus(): Promise<SetupStatus> {
  return apiFetch('/api/generate/setup-status');
}

/** Where this client's content ships: their Astro repo or their DNN site. One per
 *  client — the generator's output format follows from it. '' = not chosen yet. */
export type ContentPlatform = 'astro' | 'dnn' | '';
/** The output format each platform consumes. */
export const PLATFORM_FORMAT: Record<'astro' | 'dnn', OutputFormat> = { astro: 'astro_md', dnn: 'html' };
export function getContentPlatform(): Promise<{ platform: ContentPlatform }> {
  return apiFetch('/api/generate/platform');
}
export async function setContentPlatform(platform: ContentPlatform): Promise<void> {
  await apiFetch('/api/generate/platform', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ platform }) });
}
export async function setAssetFolder(assetFolder: string): Promise<void> {
  await apiFetch('/api/generate/site-config', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ assetFolder }) });
}

export interface GithubConfig {
  repo: string; installationId: string; branch: string;
  blogPath: string; articlePath: string; pagesPath: string; legalPath: string;
  /** Experts' author pages; the folder under src/content/pages is the URL. */
  authorsPath: string;
  appConfigured?: boolean;
}
export async function getGithubConfig(): Promise<GithubConfig> {
  return apiFetch('/api/generate/github');
}
export interface GithubCheck {
  ok: boolean; repo?: string; branch?: string;
  branches: string[]; defaultBranch: string;
  /** The installation id GitHub says owns this repo — what SHOULD be stored. */
  suggestedInstallationId: string;
  problems: string[];
}
export function checkGithubConfig(): Promise<GithubCheck> {
  return apiFetch('/api/generate/github/check');
}
export async function setGithubConfig(cfg: Omit<GithubConfig, 'appConfigured'>): Promise<void> {
  await apiFetch('/api/generate/github', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(cfg) });
}
/** `force` bypasses the site-page front-matter lint (409 `frontmatter_invalid`)
    once the operator has read the warning and still wants the file committed. */
export async function pushToSite(input: { body: string; kind: 'blog' | 'case_study' | 'site_page'; draft: boolean; publishDate?: string; path?: string; articleId?: string; force?: boolean }): Promise<{ url: string; path: string; draft: boolean }> {
  return apiFetch('/api/generate/push', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}

export type TemplateType = 'case_study_html' | 'case_study_astro' | 'blog_html' | 'blog_astro';
export async function getClientTemplates(): Promise<{ templates: Record<TemplateType, string> }> {
  return apiFetch('/api/generate/templates');
}
export async function setClientTemplate(type: TemplateType, body: string): Promise<void> {
  await apiFetch('/api/generate/templates', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type, body }) });
}

export interface RoamConfig { tokenSet: boolean; signingSecretSet: boolean; dialIn: string; webhookUrl: string }
export async function getRoamConfig(): Promise<RoamConfig> {
  return apiFetch('/api/admin/roam');
}

/* ---- monthly report email (per client) ------------------------------- */
export type NotifMetric = 'sessions' | 'conversions' | 'leads' | 'adSpend';
export interface NotifGuardrail { metric: NotifMetric; pct: number; direction: 'drop' | 'either' }
export interface NotifMember { userId: string; name: string; email: string }
export interface NotifConfig {
  enabled: boolean;
  recipients: string[];
  guardrails: NotifGuardrail[];
  lastSent: string | null;
  heldAt: string | null;
  heldReason: string | null;
  members: NotifMember[];
  metrics: { metric: NotifMetric; label: string }[];
}
export function getNotificationConfig(orgId: string): Promise<NotifConfig> {
  return apiFetch(`/api/notifications/${encodeURIComponent(orgId)}`);
}
export function setNotificationConfig(orgId: string, input: { enabled: boolean; recipients: string[]; guardrails: NotifGuardrail[] }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/notifications/${encodeURIComponent(orgId)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function previewReport(orgId: string): Promise<{ subject: string; html: string }> {
  return apiFetch(`/api/notifications/${encodeURIComponent(orgId)}/preview`, { method: 'POST' });
}
export function sendReportNow(orgId: string): Promise<{ ok: boolean; recipients?: number; error?: string }> {
  return apiFetch(`/api/notifications/${encodeURIComponent(orgId)}/send-now`, { method: 'POST' });
}
export function releaseHeldReport(orgId: string): Promise<{ ok: boolean; recipients?: number; error?: string }> {
  return apiFetch(`/api/notifications/${encodeURIComponent(orgId)}/release`, { method: 'POST' });
}
export function skipHeldReport(orgId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/notifications/${encodeURIComponent(orgId)}/skip`, { method: 'POST' });
}

/* ---- content gaps → client Q&A -------------------------------------- */
export function sendGapsToClient(input: { topic: string; questions: string[]; gaps: string[]; sourceIds?: string[]; expertId?: string }): Promise<{ ok: boolean; articleId: string }> {
  return apiFetch('/api/gaps/send', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export interface GapQuestion {
  id: string; question: string; answer: string;
  status: 'pending' | 'answered' | 'declined';
  /** Why it was declined, when it was. */
  declinedReason?: string;
}
export interface GapArticle {
  articleId: string; title: string; status: 'sent' | 'answered'; sentAt: string;
  /** What the answers become: FAQs for a page, or material for an article. */
  kind: 'faq' | 'blog';
  total: number; answered: number;
  /** Questions the client chose not to answer — settled, but not answered. */
  declined: number;
  gaps: GapQuestion[];
}
export function getGapArticles(): Promise<{ items: GapArticle[] }> {
  return apiFetch('/api/gaps');
}
/** An open question set as the report and the dashboard see it. */
export interface OpenGapSet {
  articleId: string; clientId: string; clientName: string; title: string; short: string;
  sentAt: string; remindedAt: string | null; reminders: number; sentBy: string;
  total: number; answered: number; declined: number; pending: number; lastActivity: string | null;
}
export function getOpenGapSets(): Promise<{ items: OpenGapSet[] }> { return apiFetch('/api/gaps/open'); }
export function getOutstandingGaps(): Promise<{ items: OpenGapSet[] }> { return apiFetch('/api/admin/gaps/outstanding'); }
export function remindGapSetNow(articleId: string): Promise<{ sent: boolean; reason?: string }> {
  return apiFetch(`/api/admin/gaps/${encodeURIComponent(articleId)}/remind`, { method: 'POST' });
}
export function closeGapSetNow(articleId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/gaps/${encodeURIComponent(articleId)}/close`, { method: 'POST' });
}
export function answerGap(gapId: string, answer: string): Promise<{ ok: boolean; allAnswered: boolean }> {
  return apiFetch(`/api/gaps/${encodeURIComponent(gapId)}/answer`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ answer }) });
}
/** Reasons offered for skipping a question — kept short so they are actually picked. */
export const GAP_DECLINE_REASONS = ["I don't know", 'Not relevant to us', 'Rather not say'] as const;
export function declineGap(gapId: string, reason: string): Promise<{ ok: boolean; allAnswered: boolean }> {
  return apiFetch(`/api/gaps/${encodeURIComponent(gapId)}/decline`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ reason }) });
}
export function reopenGap(gapId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/gaps/${encodeURIComponent(gapId)}/reopen`, { method: 'POST' });
}

// Read-only Authenticity Vault size for the client budget bar (no vault content).
export interface VaultStats { sourceCount: number; totalTokens: number; byKind: Record<string, { count: number; tokens: number }> }
export function getVaultStats(): Promise<VaultStats> {
  return apiFetch('/api/content/vault-stats');
}

/* ---- AI crawler telemetry (trafficIQ > AI Search) --------------------- */
export interface AiCrawlLivePage { path: string; count: number; vendors: string; lastSeen: string; aiLeads: number }
export interface AiCrawlReport {
  configured: boolean;
  firstEventAt?: string | null;
  windowDays?: number;
  stats?: { live30: number; index30: number; pagesIndexed: number; errors30: number; aiLeads30: number };
  livePages?: AiCrawlLivePage[];
  blindSpots?: { path: string; count: number; lastSeen: string }[];
  botErrors?: { path: string; status: number; count: number }[];
}
export function getAiCrawl(): Promise<AiCrawlReport> {
  return apiFetch('/api/ai-crawl');
}

/* ---- Signal Return (Google Ads feedback loop) ------------------------- */
export interface SignalCheck { ok: boolean; detail: string }
export interface SignalProbe { exchange: SignalCheck; scopes: { adwords: SignalCheck; datamanager: SignalCheck }; adsApi: SignalCheck; dataManager: SignalCheck }
export function getSignalProbe(): Promise<SignalProbe> {
  return apiFetch('/api/signal/probe');
}

export type SignalTiers = Record<'1' | '2' | '3' | '4' | '5', number>;
export interface SignalConfig {
  enabled: boolean; mode: 'off' | 'validate_only' | 'report_only' | 'live';
  agreementUpdated: boolean; conversionActionId: string; tier5ActionId: string;
  rosterTier: string; experimentActive: boolean;
}
export interface SignalValueTable {
  id: string; version: number; tiers: SignalTiers | null;
  scaleBasis: Record<string, number | string> | null;
  status: 'active' | 'pending_approval' | 'retired';
  createdBy: string | null; approvedBy: string | null; note: string; createdAt: string;
}
export interface SignalEventRow {
  tq_lead_id: string; lead_uid: string; score: number | null; value: number | null;
  status: string; attempts: number; last_error: string | null;
  event_at: string | null; last_updated_at: string | null; sent_at: string | null;
}
export interface SignalState {
  config: SignalConfig | null;
  valueTables: SignalValueTable[];
  events: SignalEventRow[];
  counts: Record<string, number>;
  seedIndex: SignalTiers;
}
export function getSignalState(orgId: string): Promise<SignalState> {
  return apiFetch(`/api/signal/${encodeURIComponent(orgId)}`);
}
export function setSignalConfig(orgId: string, input: Omit<SignalConfig, 'experimentActive'>): Promise<{ ok: boolean }> {
  return apiFetch(`/api/signal/${encodeURIComponent(orgId)}/config`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function saveSignalValueTable(orgId: string, input: { tiers: SignalTiers; scaleBasis?: Record<string, number | string>; note?: string }): Promise<{ ok: boolean; status: string; version: number; error?: string }> {
  return apiFetch(`/api/signal/${encodeURIComponent(orgId)}/value-table`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function approveSignalValueTable(orgId: string, id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/signal/${encodeURIComponent(orgId)}/value-table/${encodeURIComponent(id)}/approve`, { method: 'POST' });
}
export function flushSignal(orgId: string): Promise<{ ok: boolean; counts: Record<string, number> }> {
  return apiFetch(`/api/signal/${encodeURIComponent(orgId)}/flush`, { method: 'POST' });
}
export interface AdsConversionAction { id: string; name: string; type: string; primary: boolean }
export function getSignalConversionActions(orgId: string): Promise<{ customerId: string; actions: AdsConversionAction[]; error?: string }> {
  return apiFetch(`/api/signal/${encodeURIComponent(orgId)}/conversion-actions`);
}

/* ---- D1 backups (admin) ---------------------------------------------- */
export interface BackupFile { key: string; size: number; uploaded: string }
export interface EmailLogRow {
  id: string; kind: string; clientId: string | null; clientName: string | null; recipients: string;
  subject: string; ok: boolean; status: number | null; error: string | null; createdAt: string;
}
export function getEmailLog(): Promise<{ emails: EmailLogRow[] }> {
  return apiFetch('/api/admin/emails');
}
export function sendTestEmail(): Promise<{ ok: boolean; to: string[]; error?: string }> {
  return apiFetch('/api/admin/emails/test', { method: 'POST' });
}

/* ---- AI models (admin) ---------------------------------------------------- */
export const AI_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export interface AiProfile { model: string; effort?: (typeof AI_EFFORTS)[number] }
export interface AiTierUsage { model: string; calls: number; failures: number; inputTokens: number; outputTokens: number; costUsd: number; avgMs: number; lastAt: string }
export interface AiTierRow { tier: string; label: string; purpose: string; defaults: AiProfile; override: AiProfile | null; effective: AiProfile; usage: AiTierUsage[] }
export interface AiModelsPayload { tiers: AiTierRow[]; recentErrors: { tier: string; purpose: string; model: string; error: string; at: string }[] }
export function getAiModels(): Promise<AiModelsPayload> { return apiFetch('/api/admin/ai'); }
export function setAiModel(tier: string, profile: AiProfile): Promise<{ ok: boolean; effective: AiProfile }> {
  return apiFetch(`/api/admin/ai/${encodeURIComponent(tier)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(profile) });
}
export function resetAiModel(tier: string): Promise<{ ok: boolean; effective: AiProfile }> {
  return apiFetch(`/api/admin/ai/${encodeURIComponent(tier)}`, { method: 'DELETE' });
}
export function testAiModel(tier: string): Promise<{ ok: boolean; model?: string; reply?: string; ms?: number; error?: string }> {
  return apiFetch(`/api/admin/ai/${encodeURIComponent(tier)}/test`, { method: 'POST' });
}
export interface ClientCampaign { id: string; name: string; status: string | null; cost90d: number; lastDate: string | null }
/** Every campaign the sync has stored for a client, unfiltered — the source for the exclusion checklist. */
export function getClientCampaigns(orgId: string): Promise<{ campaigns: ClientCampaign[] }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/campaigns`);
}
export function getBackups(): Promise<{ configured: boolean; backups: BackupFile[] }> {
  return apiFetch('/api/admin/backups');
}
export function backupNow(): Promise<{ ok?: boolean; key?: string; bytes?: number; error?: string }> {
  return apiFetch('/api/admin/backups/now', { method: 'POST' });
}
export async function downloadBackup(key: string): Promise<void> {
  const url = await apiBlobUrl(`/api/admin/backups/${encodeURIComponent(key)}/download`);
  const a = document.createElement('a');
  a.href = url; a.download = key;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

/* ---- manual add + CSV import of leads (admin) ------------------------ */
export interface ManualLeadInput {
  first_name?: string; last_name?: string; full_name?: string; email?: string; phone?: string;
  company?: string; address?: string; city?: string; state?: string; zip?: string;
  message?: string; receivedAt?: string;
}
export function addManualLead(input: ManualLeadInput & { source: string; channel: string; grade: boolean }): Promise<{ ok: boolean }> {
  return apiFetch('/api/leads/manual', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
/** Admin: leads with content but no AI score that a backfill would touch (imported / webhook leads only). */
export function getUnscoredLeadCount(): Promise<{ count: number }> { return apiFetch('/api/leads/unscored'); }
/** Admin: grade a batch of unscored leads; loop until remaining is 0. */
export function gradeMissingLeads(): Promise<{ done: number; remaining: number; skipped: string | null }> {
  return apiFetch('/api/leads/grade-missing', { method: 'POST' });
}
/** Admin: remove a lead for good. The Worker records it so the hourly sync does not bring it back. */
export async function deleteLead(uid: string): Promise<void> {
  await apiFetch(`/api/leads/${encodeURIComponent(uid)}`, { method: 'DELETE' });
}
export function importLeads(input: { rows: ManualLeadInput[]; source: string; channel: string; grade: boolean }): Promise<{ ok: boolean; inserted: number; graded: number }> {
  return apiFetch('/api/leads/import', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}

/* ---- Wufoo / Formspree direct lead capture (no Zapier/BigQuery) ------- */
export interface LeadWebhookUrls { wufoo: string; formspree: string; nimbata: string }
export interface LeadWebhookConfig {
  enabled: boolean;
  gradePrompt: string;
  handshake: string;
  overrides: Record<string, string>;
  urls: LeadWebhookUrls;
  defaultPrompt: string;
  /** Nimbata call capture — its own switch; while on, BigQuery call rows for this client are skipped. */
  nimbata: boolean;
  callGradePrompt: string;
  defaultCallPrompt: string;
  nimbataSample: string;
  nimbataSampleAt: string;
}
export function getLeadWebhook(orgId: string): Promise<LeadWebhookConfig> {
  return apiFetch(`/api/lead-webhook/${encodeURIComponent(orgId)}`);
}
export function setLeadWebhook(orgId: string, input: { enabled: boolean; gradePrompt: string; overrides: Record<string, string>; nimbata?: boolean; callGradePrompt?: string }): Promise<{ ok: boolean; urls: LeadWebhookUrls }> {
  return apiFetch(`/api/lead-webhook/${encodeURIComponent(orgId)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function rotateLeadWebhook(orgId: string): Promise<{ ok: boolean; handshake: string; urls: LeadWebhookUrls }> {
  return apiFetch(`/api/lead-webhook/${encodeURIComponent(orgId)}/rotate`, { method: 'POST' });
}

/* ---- Roam transcript inbox (Admin → Content) ------------------------- */
// Magic Minutes transcripts (incl. unscheduled Meet/kickoff calls) captured by
// the webhook. Pull a chosen one into a client's Vault and/or a Story.
export interface InboxTranscript {
  id: string;
  title: string;
  participants: { name: string; email: string }[];
  startedAt: string;
  preview: string;
  hasSummary: boolean;
  status: 'new' | 'attached';
  attachedKind: string | null;
  attachedClientId: string | null;
  attachedAt: string | null;
}
export function getTranscriptInbox(): Promise<{ items: InboxTranscript[] }> {
  return apiFetch('/api/roam/inbox');
}
export interface InboxTranscriptDetail {
  id: string;
  title: string;
  participants: { name: string; email: string }[];
  startedAt: string;
  text: string;
  summary: string;
  status: string;
}
export function getTranscriptDetail(id: string): Promise<InboxTranscriptDetail> {
  return apiFetch(`/api/roam/inbox/${encodeURIComponent(id)}`);
}
export function transcriptToVault(id: string, orgId: string): Promise<{ ok: boolean; sourceId: string }> {
  return apiFetch(`/api/roam/inbox/${encodeURIComponent(id)}/to-vault`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ orgId }),
  });
}
export function transcriptToStory(id: string, orgId: string, title?: string): Promise<{ ok: boolean; articleId: string }> {
  return apiFetch(`/api/roam/inbox/${encodeURIComponent(id)}/to-story`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ orgId, title }),
  });
}
export function dismissTranscript(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/roam/inbox/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
}

export async function getHouseStyle(): Promise<{ body: string }> {
  return apiFetch('/api/generate/house-style');
}
export async function setHouseStyle(body: string): Promise<void> {
  await apiFetch('/api/generate/house-style', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ body }) });
}

export async function getTritonLinkMap(): Promise<{ value: string }> {
  return apiFetch('/api/generate/link-map');
}
export async function setTritonLinkMap(value: string): Promise<void> {
  await apiFetch('/api/generate/link-map', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ value }) });
}

/* ---- client business profile (admin) --------------------------------- */

export interface ClientLocation { label: string; address: string; phone: string; city: string; gatherupClientId: string }
/** One SEO focus page. The list's order is its priority (index 0 = #1). */
export interface SeoFocusPage { url: string; anchor: string; keywords: string }
export const SEO_FOCUS_MAX = 7;
export interface ClientProfile {
  bizType: string;
  address: string;
  phone: string;
  website: string;
  /** Where a rebuild is previewed before launch; blank once the site is live. */
  stagingUrl: string;
  serviceAreas: string;
  services: string;
  notes: string;
  seoNotes: string;
  /** Ranked pages the SEO team wants supporting content for, at most SEO_FOCUS_MAX. */
  seoFocusPages: SeoFocusPage[];
  locations: ClientLocation[];
  assetFolder: string;
}
export function setClientProfile(orgId: string, profile: ClientProfile): Promise<void> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/profile`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(profile),
  });
}

/* ---- client data connections (admin) --------------------------------- */

/** The per-client wiring: which BigQuery table, Google Ads account, GA4
    property and Triton Reviews account it reads, plus AI-interview settings.
    Separate endpoint from the business profile above. */
export interface ClientConnections {
  bqTable: string;
  googleAdsCustomerId: string;
  ga4PropertyId: string;
  gscSiteUrl: string;
  /** Which Google service account reads GA4 + Search Console: '1' | '2'. */
  googleSa: string;
  gatherupClientId: string;
  reviewsStaleDays: string;
  monthlyAdSpendGoal: string;
  adBillingDay: string;
  contentReminderEnabled: boolean;
  contentReminderDays: string;
  contentReminderInterval: string;
  aiInterviewEnabled: boolean;
  /** contentIQ AI hero images. Off by default; unrelated to Expert Studio. */
  aiImagesEnabled: boolean;
  interviewStyle: string;
  contentManagerId: string;
  team: TeamAssignments;
  selfServiceEnabled: boolean;
  /** May this client's page FAQs be written to their Astro repo. Off until their site renders them. */
  faqPushEnabled: boolean;
  /** Hold the client's own FAQ edits for Triton approval before they can reach a page. */
  faqReviewHold: boolean;
  /** May conversationIQ answer from this client's approved FAQs. */
  faqChatEnabled: boolean;
  /** Show the trafficIQ "Compare:" filter to this client's users. */
  trafficCompareEnabled: boolean;
  /** Google Ads campaign ids left OUT of this client's campaignIQ (shared account). Empty = all included. */
  excludedCampaignIds: string[];
  isDemo: boolean;
}

/* The connections save is a PATCH: a key absent from the body is left alone by
   the Worker (worker/client-connections.ts), so each tab of the client page sends
   only the fields it owns. `connectionsPayload` is the one place form strings
   (numbers typed into inputs) become wire values; `patchClientConnections`
   sends whatever subset it is handed. */
export interface ConnectionsPayload {
  name?: string;
  bqTable: string;
  googleAdsCustomerId: string;
  aiInterviewEnabled: boolean;
  aiImagesEnabled: boolean;
  contentManagerId: string | null;
  team: TeamAssignments;
  selfServiceEnabled: boolean;
  faqPushEnabled: boolean;
  faqReviewHold: boolean;
  faqChatEnabled: boolean;
  trafficCompareEnabled: boolean;
  excludedCampaignIds: string[];
  isDemo: boolean;
  interviewStyle: string;
  ga4PropertyId: string | null;
  gscSiteUrl: string | null;
  googleSa: string;
  gatherupClientId: string | null;
  reviewsStaleDays: number;
  monthlyAdSpendGoal: number | null;
  adBillingDay: number | null;
  contentReminderEnabled: boolean;
  contentReminderDays: number;
  contentReminderInterval: number;
}

export function connectionsPayload(c: ClientConnections): Omit<ConnectionsPayload, 'name'> {
  return {
    bqTable: c.bqTable,
    googleAdsCustomerId: c.googleAdsCustomerId,
    aiInterviewEnabled: c.aiInterviewEnabled,
    aiImagesEnabled: c.aiImagesEnabled,
    contentManagerId: c.contentManagerId || null,
    team: c.team,
    selfServiceEnabled: c.selfServiceEnabled,
    faqPushEnabled: c.faqPushEnabled,
    faqReviewHold: c.faqReviewHold,
    faqChatEnabled: c.faqChatEnabled,
    trafficCompareEnabled: c.trafficCompareEnabled,
    excludedCampaignIds: c.excludedCampaignIds,
    isDemo: c.isDemo,
    interviewStyle: c.interviewStyle,
    ga4PropertyId: c.ga4PropertyId || null,
    gscSiteUrl: c.gscSiteUrl || null,
    googleSa: c.googleSa === '2' ? '2' : '1',
    gatherupClientId: c.gatherupClientId || null,
    reviewsStaleDays: Number(c.reviewsStaleDays) || 30,
    monthlyAdSpendGoal: c.monthlyAdSpendGoal.trim() === '' ? null : Number(c.monthlyAdSpendGoal),
    adBillingDay: c.adBillingDay.trim() === '' ? null : Number(c.adBillingDay),
    contentReminderEnabled: c.contentReminderEnabled,
    contentReminderDays: Number(c.contentReminderDays) || 30,
    contentReminderInterval: Number(c.contentReminderInterval) || 7,
  };
}

export function patchClientConnections(orgId: string, patch: Partial<ConnectionsPayload>): Promise<void> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

/** Every field at once — what the original single-form dialog sent. */
export function setClientConnections(orgId: string, name: string, c: ClientConnections): Promise<void> {
  return patchClientConnections(orgId, { name, ...connectionsPayload(c) });
}

/* ---- admin: blog-topic approval queue (cross-client) ----------------- */
export interface AdminBlogTopic {
  id: string; clientId: string; clientName: string; topic: string; questions: string[];
  status: 'pending' | 'approved' | 'rejected'; createdAt: string;
  /** The page the topic supports and the batch it arrived in (empty for single topics). */
  supportUrl: string; supportAnchor: string; batchId: string; keywords: string; requestedBy: string;
  optimizationManagerId: string | null; optimizationManagerName: string | null;
  contentManagerId: string | null; contentManagerName: string | null;
  /** The contentIQ Editor, when assigned — the approval email goes to them instead of the Content Manager. */
  editorId: string | null; editorName: string | null;
}
export function getAdminBlogTopics(): Promise<{ topics: AdminBlogTopic[]; viewerId: string }> {
  return apiFetch('/api/admin/blog-topics');
}
/** The reviewer's edits to a topic before approving it (any client). */
export function updateAdminBlogTopic(id: string, patch: { topic?: string; questions?: string[]; keywords?: string }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/blog-topics/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) });
}
/** Approve or reject every still-pending topic in a batch; one email to the writer. */
export function setAdminBlogTopicBatchStatus(batchId: string, status: 'approved' | 'rejected'): Promise<{ ok: boolean; updated: number }> {
  return apiFetch(`/api/admin/blog-topics/batch/${encodeURIComponent(batchId)}/status`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ status }) });
}
export function setAdminBlogTopicStatus(id: string, status: 'approved' | 'rejected' | 'pending'): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/blog-topics/${encodeURIComponent(id)}/status`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ status }),
  });
}

/* ---- client access / roles ------------------------------------------- */
export type ClientRoleName = 'admin' | 'client_manager' | 'contributor' | 'expert';
export interface MyAccess {
  role: ClientRoleName; isAdmin: boolean; canReports: boolean; canContent: boolean; canStudio: boolean;
  /** trafficIQ period comparison: admins always, clients only when opted in. */
  canCompare: boolean;
  /** True when an admin asked for a client's view of this account. */
  viewingAsClient?: boolean;
  /** Ask tritonIQ: admins now, client managers once the guardrails are proven. */
  canAssistant?: boolean;
  /** Which engine dictates spoken answers (rollout switch; absent = browser). */
  dictationEngine?: DictationEngine;
}
export function getMyAccess(asClient = false): Promise<MyAccess> { return apiFetch(asClient ? '/api/me?as=client' : '/api/me'); }

/* ---- Dictation (Whisper on Workers AI) ------------------------------------ */
export type DictationEngine = 'browser' | 'whisper';
/** One recorded chunk in, its text out. `ms` is the chunk's length, for the usage log. */
export async function transcribeChunk(blob: Blob, ms: number): Promise<string> {
  const r = await apiFetch<{ text: string }>('/api/dictation', {
    method: 'POST', headers: { 'content-type': blob.type || 'audio/webm', 'x-audio-ms': String(Math.round(ms)) }, body: blob,
  });
  return r.text;
}
/** Fillers out, punctuation in, no word added — or the same text back. */
export function tidyDictation(text: string): Promise<{ text: string; tidied: boolean }> {
  return apiFetch('/api/dictation/tidy', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) });
}
export interface DictationAdmin {
  available: boolean; switch: string;
  usage30d: { calls: number; failures: number; minutes: number; costUsd: number; avgMs: number };
}
export function getDictationAdmin(): Promise<DictationAdmin> { return apiFetch('/api/admin/dictation'); }

/* ---- Rollout switches (admin) --------------------------------------------- */
export interface RolloutState {
  name: string; options: string[]; value: string;
  overrides: { orgId: string; clientName: string; value: string }[];
}
export function getRollout(name: string): Promise<RolloutState> { return apiFetch(`/api/admin/rollout/${encodeURIComponent(name)}`); }
export function setRolloutValue(name: string, value: string, orgId?: string): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/rollout/${encodeURIComponent(name)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ value, orgId }) });
}
export function clearRolloutOverride(name: string, orgId: string): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/rollout/${encodeURIComponent(name)}/${encodeURIComponent(orgId)}`, { method: 'DELETE' });
}

export interface ClientUser { userId: string; name: string; email: string; role: 'client_manager' | 'contributor' | 'expert' }
export interface ClientUserInvite { email: string; role: ClientUser['role']; invitedAt: string }
export function getClientUsers(orgId: string): Promise<{ users: ClientUser[]; invites: ClientUserInvite[] }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/users`);
}
/* Two ways to add a person. Invite is the default — Clerk emails a link and no
   secret passes through Triton. Create-with-password is for clients whose mail
   security breaks invite links; the response carries the generated password
   exactly once when none was supplied. */
export function inviteClientUser(orgId: string, body: { email: string; role: string }): Promise<{ ok: boolean; email: string; role: string }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/users/invite`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
}
export function revokeClientInvite(orgId: string, email: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/users/invites/${encodeURIComponent(email)}`, { method: 'DELETE' });
}
export function createClientUser(
  orgId: string,
  body: { email: string; firstName: string; lastName: string; role: string; password?: string },
): Promise<{ ok: boolean; userId: string; email: string; role: string; temporaryPassword?: string }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/users`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
}
export function removeClientUser(orgId: string, userId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}
export function setClientUserRole(orgId: string, userId: string, role: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/role`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ role }),
  });
}

/* Triton-admin-only: the client's monthly ad-spend goal + billing day (campaignIQ). */
export function getCampaignGoal(): Promise<{ admin: boolean; goal: number | null; billingDay: number | null }> {
  return apiFetch('/api/campaigns/goal');
}

/* ---- staff (system-wide admins) -------------------------------------- */

export interface StaffMember {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  isSelf: boolean;
}
export function getStaff(): Promise<{ staff: StaffMember[] }> {
  return apiFetch('/api/admin/staff');
}
export function updateStaffProfile(userId: string, patch: { firstName: string; lastName: string }): Promise<{ ok: boolean; name: string }> {
  return apiFetch(`/api/admin/staff/${encodeURIComponent(userId)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) });
}
export function addStaff(email: string): Promise<{ ok: boolean; id?: string; promoted?: boolean; invited?: boolean }> {
  return apiFetch('/api/admin/staff', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}
export function removeStaff(userId: string): Promise<void> {
  return apiFetch(`/api/admin/staff/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}
export function syncStaffAccess(): Promise<{ ok: boolean; admins: number; orgs: number; added: number; raised: number; failed: number }> {
  return apiFetch('/api/admin/staff/sync-access', { method: 'POST' });
}
export function createDemoAccount(): Promise<{ orgId: string; name: string }> {
  return apiFetch('/api/admin/demo/create', { method: 'POST' });
}
export function seedDemoData(orgId: string): Promise<{ ok: boolean; leads: number }> {
  return apiFetch('/api/admin/demo/seed', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ orgId }) });
}
export interface DemoContentCounts { sources: number; articles: number; topics: number; blogTopics: number; covered: number; socialPosts: number }
/** (Re)seed the demo account's contentIQ fixtures: vault, articles, topics, blog queue, social. */
export function seedDemoContent(orgId: string): Promise<{ ok: boolean } & DemoContentCounts> {
  return apiFetch('/api/admin/demo/content', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ orgId }) });
}
/** One batch of AI summaries for demo leads that lack one; loop until remaining is 0. */
export function summarizeDemoLeads(orgId: string): Promise<{ done: number; remaining: number; skipped: string | null }> {
  return apiFetch('/api/admin/demo/summaries', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ orgId }) });
}

/* ---- admin account switcher (searchable) ----------------------------- */
export interface AdminOrg { orgId: string; name: string }
export function getAdminOrgs(): Promise<{ orgs: AdminOrg[] }> {
  return apiFetch('/api/admin/orgs');
}
export function createClientOrg(name: string): Promise<{ orgId: string; name: string }> {
  return apiFetch('/api/admin/orgs', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name }) });
}

/* ---- content admin (cross-client) ------------------------------------ */

export interface Manager {
  id: string;
  name: string;
  imageUrl?: string;
}

// Per-client team roles (beyond Content Manager, which has its own field).
export const TEAM_ROLES = [
  /* The client's main publisher: processes Vault material into blogs, case
     studies and knowledge sessions, and takes the content hand-off emails over
     from the Content Manager when assigned. Rendered through IqName so the IQ
     keeps its brand treatment wherever the label appears. */
  { key: 'contentEditor', label: 'contentIQ Editor' },
  { key: 'accountManager', label: 'Account Manager' },
  { key: 'optimizationManager', label: 'Optimization Manager' },
  { key: 'paidAdsManager', label: 'Paid Ads Manager' },
  { key: 'brandStrategyManager', label: 'Brand / Strategy Manager' },
  { key: 'designDevManager', label: 'Design / Dev Manager' },
] as const;
export type TeamRoleKey = (typeof TEAM_ROLES)[number]['key'];
export type TeamAssignments = Record<TeamRoleKey, string>;

export interface TeamMemberCard { role: string; name: string; imageUrl: string }
export function getClientTeam(): Promise<{ team: TeamMemberCard[] }> {
  return apiFetch('/api/overview/team');
}
export function reassignAccounts(fromUserId: string, toUserId: string): Promise<{ ok: boolean; moved: number }> {
  return apiFetch('/api/admin/reassign', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ fromUserId, toUserId }) });
}

/* ---- Expert Studio (client self-service jobs + galleries) ------------- */

export interface StudioStatus {
  enabled: boolean; memberEnabled?: boolean;
  /** Where this client's content ships. '' when nobody has chosen yet. */
  platform?: ContentPlatform;
}
export function getStudioStatus(): Promise<StudioStatus> {
  return apiFetch('/api/studio/status');
}
export function getStudioFeedUrl(): Promise<{ url: string }> {
  return apiFetch('/api/studio/feed-url');
}
export interface StudioLead { id: string; name: string; city: string; state: string; detail: string; channel: string }
export function searchStudioLeads(q: string): Promise<{ leads: StudioLead[] }> {
  return apiFetch(`/api/studio/leads?q=${encodeURIComponent(q)}`);
}
export interface StudioJob { id: string; contactName: string; city: string; state: string; projectType: string; leadId: string; createdAt: string; galleryCount: number; publishedGalleries: number }
export function getStudioJobs(): Promise<{ jobs: StudioJob[] }> {
  return apiFetch('/api/studio/jobs');
}
export function createStudioJob(input: { contactName: string; city?: string; state?: string; projectType?: string; leadId?: string }): Promise<{ id: string }> {
  return apiFetch('/api/studio/jobs', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function deleteStudioJob(id: string): Promise<void> {
  return apiFetch(`/api/studio/jobs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export interface StudioStory { id: string; title: string; stage: number; statusDate: string; source: string; scheduledAt: string | null; hasDraft: boolean; hasTranscript: boolean }
export function getStudioStory(jobId: string): Promise<{ story: StudioStory | null }> {
  return apiFetch(`/api/studio/jobs/${encodeURIComponent(jobId)}/story`);
}
export function createStudioStory(jobId: string): Promise<{ id: string }> {
  return apiFetch(`/api/studio/jobs/${encodeURIComponent(jobId)}/story`, { method: 'POST' });
}
/** Standard = a photo set (global/gallery block). Before/after = labelled pairs
 *  (global/before-after block). A gallery's kind is fixed when it's created. */
export type GalleryKind = 'standard' | 'before_after';
/* ---- client FAQs (Expert Studio → Vault) ------------------------------- */
/* Stored in the Vault, one 'qa' source per FAQ — so each has a real id and can
   be edited or deleted on its own, in here and in the vault itself. */
export interface FaqPair { question: string; answer: string }
/** One vault source per FAQ, so each has its own id to view, edit and delete. */
export interface FaqRow extends FaqPair { id: string; createdAt: string }
export function getFaqs(): Promise<{ faqs: FaqRow[] }> {
  return apiFetch('/api/faq');
}
/* Every write returns the whole list, so the editor never has to reconcile its
   own copy with the server's. */
export function addFaq(pair: FaqPair): Promise<{ faqs: FaqRow[] }> {
  return apiFetch('/api/faq', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(pair) });
}
export function updateFaq(id: string, pair: FaqPair): Promise<{ faqs: FaqRow[] }> {
  return apiFetch(`/api/faq/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(pair) });
}
export function deleteFaq(id: string): Promise<{ faqs: FaqRow[] }> {
  return apiFetch(`/api/faq/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
/* ---- page FAQs ---------------------------------------------------------
   A Q&A lives in the Vault; a PLACEMENT says which page shows it. Every write
   returns the page's whole list, so the editor never reconciles its own copy. */

export interface PageFaq extends FaqRow {
  display: boolean;
  sort: number;
  /** approved | awaiting_review — only approved entries can reach the page. */
  state: string;
  authoredBy: string;
}
export interface FaqPageRow {
  route: string; label: string; name: string; section: string;
  total: number; shown: number; waiting: number;
}
export interface FaqPageDetail {
  route: string; label: string; name: string; items: PageFaq[];
  pushEnabled: boolean; reviewHold: boolean; canApprove: boolean;
  pending: boolean; lastPushedAt: string; lastPushedUrl: string;
}

export function getFaqPages(): Promise<{
  sections: { name: string; pages: FaqPageRow[] }[];
  pushEnabled: boolean; reviewHold: boolean; needsSitemap: boolean;
}> {
  return apiFetch('/api/faq/pages');
}
const pageQ = (route: string) => `?page=${encodeURIComponent(route)}`;
export function getFaqPage(route: string): Promise<FaqPageDetail> {
  return apiFetch(`/api/faq/page${pageQ(route)}`);
}
export function addPageFaq(route: string, pair: FaqPair, expertId?: string): Promise<{ items: PageFaq[] }> {
  return apiFetch(`/api/faq/page${pageQ(route)}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...pair, expertId }) });
}
/** Display and order together: the list is sent whole, in the order it is shown. */
export function setFaqPageOrder(route: string, items: { id: string; display: boolean }[]): Promise<{ items: PageFaq[]; pending: boolean }> {
  return apiFetch(`/api/faq/page/order${pageQ(route)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ items }) });
}
/** Edit from the page. Goes back through review if the client is on hold. */
export function editPageFaq(route: string, id: string, pair: FaqPair): Promise<{ items: PageFaq[]; pending: boolean }> {
  return apiFetch(`/api/faq/page/item${pageQ(route)}&id=${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(pair) });
}
/** Off this page — the answer stays in the Vault and on any other page. */
export function removeFaqFromPage(route: string, id: string): Promise<{ items: PageFaq[] }> {
  return apiFetch(`/api/faq/page/item${pageQ(route)}&id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function approvePageFaq(route: string, id: string): Promise<{ items: PageFaq[] }> {
  return apiFetch(`/api/faq/page/approve${pageQ(route)}&id=${encodeURIComponent(id)}`, { method: 'POST' });
}
/** Has this question been answered already? Flags only — never blocks a save. */
export function similarFaqs(question: string): Promise<{
  matches: { id: string; question: string; answer: string; why: string }[];
}> {
  return apiFetch('/api/faq/similar', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ question }) });
}
/** Q&As that contradict each other. Admin only. */
export function auditFaqs(): Promise<{
  checked: number;
  conflicts: { a: { id: string; question: string; answer: string }; b: { id: string; question: string; answer: string }; why: string }[];
}> {
  return apiFetch('/api/faq/audit', { method: 'POST' });
}
/** Does the Vault already answer these? Checks only — saves nothing. */
export function checkFaqPage(route: string, questions: string[]): Promise<{
  answers: { question: string; answer: string; source: string }[];
}> {
  return apiFetch(`/api/faq/page/check${pageQ(route)}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ questions }) });
}
/** Questions worth asking about this page. Proposes only — saves nothing. */
export function suggestFaqPage(route: string): Promise<{ questions: { q: string; why: string }[]; hadPageCopy: boolean }> {
  return apiFetch(`/api/faq/page/suggest${pageQ(route)}`, { method: 'POST' });
}
/** Send a page's unanswered questions to the client. Admin only. */
export function askFaqPage(route: string, questions: string[], expertId?: string): Promise<{ ok: boolean; sent: number; drafted: number }> {
  return apiFetch(`/api/faq/page/ask${pageQ(route)}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ questions, expertId }) });
}
export function publishFaqPage(route: string): Promise<{ ok: boolean; url: string; count: number; unchanged?: boolean }> {
  return apiFetch(`/api/faq/page/publish${pageQ(route)}`, { method: 'POST' });
}

/** Questions worth asking, that this client is the one to answer. */
export function suggestFaqs(): Promise<{ questions: string[] }> {
  return apiFetch('/api/faq/suggest', { method: 'POST' });
}

/* Two levels: a Gallery has a page, and optionally Categories that each get a
   page of their own. `multiPage` is which of those a gallery is — single page
   (everything on one wall) is the default. */
export interface Gallery {
  id: string; name: string; slug: string; description: string;
  categoryCount: number; itemCount: number;
  /** First photo in the running order, or the chosen cover. '' when empty. */
  coverUrl: string; coverAssetId: string;
  route: string;
}
export interface GalleryCategory {
  id: string; name: string; slug: string; kind: GalleryKind;
  hidden: boolean;
  /** This album's own photos plus everything in its sub-albums. */
  itemCount: number;
  /** Just its own loose photos. Absent on a sub-album. */
  ownItemCount?: number;
  coverUrl: string; coverAssetId: string; route: string;
  /** Sub-albums. Always present on an album, always empty on a sub-album. */
  children: GalleryCategory[];
}
/** How this client's gallery section is shaped. Triton-staff only. */
export interface GallerySettings {
  /** How many photos this client may feature at once. */
  featuredLimit: number;
  /** Build trigger fired when this client's gallery changes, so their
      PRERENDERED pages catch up. Their gallery route is live either way. */
  deployHookUrl: string;
  /** Set while a change is waiting on the next sweep. */
  deployPendingSince: string;
  deployFiredAt: string;
  /** Refresh URL of a SERVER-RENDERED site (the DNN gallery module), called on
      every save so it drops its cached copy. */
  cacheHookUrl: string;
  /** Set while a save has not reached the site yet. */
  cachePendingSince: string;
  cacheFiredAt: string;
}
export function getGallerySettings(): Promise<GallerySettings> {
  return apiFetch('/api/studio/gallery-settings');
}
export function updateGallerySettings(patch: { deployHookUrl?: string; cacheHookUrl?: string; featuredLimit?: number }): Promise<void> {
  return apiFetch('/api/studio/gallery-settings', {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch),
  });
}

/** The public, token-guarded URL a site fetches this client's galleries from. */
export function getGalleryApiUrl(): Promise<{ url: string; token: string; client: string; orgId: string }> {
  return apiFetch('/api/studio/gallery-api');
}
/** Mint a new gallery token. The old one stops working immediately. */
export function rotateGalleryToken(): Promise<{ token: string }> {
  return apiFetch('/api/studio/gallery-api/rotate', { method: 'POST' });
}
export interface PublishState {
  /** The studio holds edits the site is not showing yet. */
  pending: boolean;
  /** When the earliest unsaved change landed. '' when nothing is pending. */
  pendingSince: string;
  /** When the site's current version was saved. '' if never. */
  publishedAt: string;
}
/** Throw away every gallery change since the last save. Not undoable. */
export function cancelGalleryChanges(): Promise<PublishState> {
  return apiFetch('/api/studio/gallery-cancel', { method: 'POST' });
}
/** Put the current state of the gallery on the site, and rebuild if a hook is set. */
export function publishGallery(): Promise<PublishState & { rebuilding: boolean; refreshed: boolean }> {
  return apiFetch('/api/studio/gallery-publish', { method: 'POST' });
}
export function getGalleries(): Promise<PublishState & {
  /** How many photos this client features, and how many they may. */
  featuredUsed: number;
  featuredLimit: number;
  routeBase: string;
  /** The client's one gallery, when they have one yet. Empty until their first
      album creates it. */
  primaryGalleryId: string;
  galleries: Gallery[];
}> {
  return apiFetch('/api/studio/gallery-sets');
}
export function getGallery(id: string): Promise<{
  gallery: Gallery;
  categories: GalleryCategory[];
}> {
  return apiFetch(`/api/studio/gallery-sets/${encodeURIComponent(id)}`);
}
/** An album, or a sub-album when `parentId` names one. The client's gallery is
    resolved server-side — there is one, and nothing here should have to say
    which. */
export function createGalleryAlbum(input: { name: string; kind?: GalleryKind; parentId?: string }): Promise<{ id: string }> {
  return apiFetch('/api/studio/gallery-albums', {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
  });
}
export function updateGalleryCategory(id: string, patch: { name?: string; slug?: string; kind?: GalleryKind; description?: string; hidden?: boolean; coverAssetId?: string | null }): Promise<void> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch),
  });
}
export function deleteGalleryCategory(id: string): Promise<void> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** One item in a gallery: a photo, or a before/after pair held as ONE row so
    reordering cannot separate the halves. */
export interface GalleryItem {
  id: string; type: 'photo' | 'pair'; sort: number;
  /** Short heading for the photo. Not the alt text, not the caption. */
  title: string;
  alt: string; caption: string; label: string; project: string;
  /** Public CDN URLs. Empty until the bytes have a public copy. */
  url: string; beforeUrl: string; afterUrl: string;
  /** For the manager's authenticated preview fallback — see AssetImg. */
  assetId: string; beforeAssetId: string; afterAssetId: string;
  /** Offered to the site for highlights outside the gallery. Capped per client. */
  featured: boolean;
}
export interface GalleryDetail {
  category: {
    id: string; name: string; slug: string; kind: GalleryKind; description: string; hidden: boolean;
    /** The contentIQ case study this album belongs to, if any. */
    articleId: string;
    /** Chosen cover photo; empty means the first item stands in. */
    coverAssetId: string;
    /** Set when this is a sub-album. */
    parentId: string;
  };
  items: GalleryItem[];
  /** Sub-albums of this album. Always empty for a sub-album. */
  children: {
    id: string; name: string; slug: string; kind: GalleryKind;
    hidden: boolean; itemCount: number; coverUrl: string; coverAssetId: string;
  }[];
}
/* Photos on a Studio project that is not an article — a social post's images.
   Gallery assets, like a case study's: stored once, public on upload, filable
   into a real album afterwards. */
export interface ProjectPhoto {
  id: string; assetId: string; filename: string; cdnUrl: string; albums: string[];
}
export function getProjectPhotos(projectId: string): Promise<{ photos: ProjectPhoto[] }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(projectId)}/photos`);
}
export async function uploadProjectPhoto(
  projectId: string, img: { file: File; width: number; height: number },
): Promise<{ id: string; assetId: string }> {
  const q = new URLSearchParams({ filename: img.file.name, w: String(img.width), h: String(img.height) });
  return apiFetch(`/api/studio/projects/${encodeURIComponent(projectId)}/photos?${q}`, {
    method: 'POST',
    headers: { 'content-type': img.file.type || 'application/octet-stream' },
    body: img.file,
  });
}
export function addGalleryPhotoToProject(projectId: string, assetId: string): Promise<{ id: string; assetId: string }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(projectId)}/photos`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ assetId }),
  });
}
export async function deleteProjectPhoto(projectId: string, rowId: string): Promise<void> {
  await apiFetch(`/api/studio/projects/${encodeURIComponent(projectId)}/photos/${encodeURIComponent(rowId)}`, { method: 'DELETE' });
}

/* Running order, sent whole. Each of these refuses with 409 and the real
   current order when the caller's list has gone stale — see worker/galleryorder.ts. */
export function reorderGalleryAlbums(setId: string, ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/gallery-sets/${encodeURIComponent(setId)}/album-order`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids }),
  });
}
export function reorderSubAlbums(parentId: string, ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(parentId)}/sub-order`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids }),
  });
}
export function reorderArticlePhotos(articleId: string, ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/photos/order`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids }),
  });
}
export function reorderProjectPhotos(projectId: string, ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(projectId)}/photos/order`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids }),
  });
}

/** Every album this client has, flat, for a "which album?" picker. */
export interface FlatAlbum {
  id: string; name: string; galleryName: string; parentName: string;
  isSubAlbum: boolean; kind: GalleryKind;
}
export function getGalleryAlbums(): Promise<{ albums: FlatAlbum[] }> {
  return apiFetch('/api/studio/gallery-albums');
}

/** Every photo this client has, flat and paged, for "add one you already have". */
export interface GalleryAssetSummary {
  id: string; url: string; width: number; height: number;
  /** Album names this photo already appears in. */
  albums: string[];
}
export function getGalleryAssets(offset = 0, limit = 60): Promise<{ assets: GalleryAssetSummary[]; nextOffset: number | null }> {
  return apiFetch(`/api/studio/gallery-assets?offset=${offset}&limit=${limit}`);
}

/** Put a photo the client already has into another album. No bytes are copied. */
export function addAssetToAlbum(categoryId: string, assetId: string): Promise<{ id: string; assetId: string; already?: boolean }> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(categoryId)}/items`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ assetId }),
  });
}

export function getGalleryDetail(id: string): Promise<GalleryDetail> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(id)}`);
}
/* Photos go live on upload — there is no publish step in this model, because the
   client's site reads the gallery on demand. Dimensions ride along because the
   site cannot afford to measure a remote image per request. */
/** Place a photo the client already has into another album. One asset, two
    items — the split in migration 0060 exists precisely for this. */
export function addExistingPhotoToAlbum(categoryId: string, assetId: string): Promise<{ id: string; already?: boolean }> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(categoryId)}/items`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ assetId }),
  });
}
export function uploadGalleryPhoto(categoryId: string, img: { file: File; width: number; height: number }): Promise<{ id: string }> {
  const q = new URLSearchParams({ filename: img.file.name, w: String(img.width), h: String(img.height) });
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(categoryId)}/items?${q}`, {
    method: 'POST', headers: { 'content-type': img.file.type || 'application/octet-stream' }, body: img.file,
  });
}
export function createGalleryPair(categoryId: string, label?: string): Promise<{ id: string }> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(categoryId)}/pairs`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ label }),
  });
}
export function uploadGallerySide(itemId: string, role: 'before' | 'after', img: { file: File; width: number; height: number }): Promise<{ assetId: string }> {
  const q = new URLSearchParams({ role, filename: img.file.name, w: String(img.width), h: String(img.height) });
  return apiFetch(`/api/studio/gallery-items/${encodeURIComponent(itemId)}/side?${q}`, {
    method: 'POST', headers: { 'content-type': img.file.type || 'application/octet-stream' }, body: img.file,
  });
}
export function updateGalleryItem(id: string, patch: { title?: string; alt?: string; caption?: string; label?: string; project?: string; featured?: boolean }): Promise<void> {
  return apiFetch(`/api/studio/gallery-items/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch),
  });
}
/** Replace a photo's bytes with the same photo turned. Same URL path, so every
    place already using it gets the turned version. */
export function rotateGalleryAsset(assetId: string, img: { file: File; width: number; height: number }): Promise<{ url: string; width: number; height: number; rev: number }> {
  const q = new URLSearchParams({ w: String(img.width), h: String(img.height) });
  return apiFetch(`/api/studio/gallery-assets/${encodeURIComponent(assetId)}/rotate?${q}`, {
    method: 'POST', headers: { 'content-type': img.file.type }, body: img.file,
  });
}
export function deleteGalleryItem(id: string): Promise<void> {
  return apiFetch(`/api/studio/gallery-items/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function reorderGalleryItems(categoryId: string, ids: string[]): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/gallery-categories/${encodeURIComponent(categoryId)}/order`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids }),
  });
}

export interface StudioProjectMeta {
  lead?: { uid: string; name: string; city: string; state: string; request: string; permission?: string };
  review?: { author: string; rating: number; date: string; content: string };
  fields?: Record<string, string>;
  draftText?: string;
  caption?: string;
  captions?: Record<string, string>;
  platforms?: string[];
  vision?: string[];
}
export interface StudioProject {
  id: string;
  type: 'article' | 'social';
  kind: 'case' | 'knowledge' | 'blog' | '';
  name: string;
  articleId: string;
  topicId: string;
  meta: StudioProjectMeta;
  /** The piece Triton wrote from this project once it is published: its final title and live URL. */
  published?: { title: string; url: string } | null;
  step: number;
  submitted: boolean;
  status: 'draft' | 'ai' | 'input' | 'review' | 'approved' | 'live';
  createdAt: string;
  updatedAt: string;
}
export function getStudioProjects(): Promise<{ projects: StudioProject[] }> {
  return apiFetch('/api/studio/projects');
}
export function createStudioProject(input: { type: string; kind?: string; name?: string; meta?: StudioProjectMeta }): Promise<{ project: StudioProject }> {
  return apiFetch('/api/studio/projects', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
/** Exit without input: the Worker deletes the project only if it is still an empty shell. */
export function discardStudioProjectIfEmpty(id: string): Promise<{ discarded: boolean }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(id)}/discard-if-empty`, { method: 'POST' });
}
export function patchStudioProject(id: string, patch: { name?: string; meta?: StudioProjectMeta; step?: number }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(patch) });
}
export function deleteStudioProject(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function submitStudioProject(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/studio/projects/${encodeURIComponent(id)}/submit`, { method: 'POST' });
}
export interface StudioLeadHit { id: string; name: string; city: string; state: string; when: string; score: number | null; channel: string; request: string }
export function studioLeadSearch(q: string): Promise<{ leads: StudioLeadHit[] }> {
  return apiFetch(`/api/studio/lead-search?q=${encodeURIComponent(q)}`);
}
export interface StudioReviewHit { author: string; rating: number; date: string; content: string }
export function studioReviewSearch(q: string): Promise<{ reviews: StudioReviewHit[] }> {
  return apiFetch(`/api/studio/review-search?q=${encodeURIComponent(q)}`);
}
export function studioSocialCaptions(input: { projectId?: string; description?: string; tone?: string }): Promise<{ vision: string[]; captions: { platform: string; text: string }[] }> {
  return apiFetch('/api/studio/social-captions', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export interface StudioSocialPost { id: string; caption: string; captions: Record<string, string>; platforms: string[]; images: string[]; timing: string; status: string; createdAt: string }
export function studioSocialQueue(): Promise<{ posts: StudioSocialPost[] }> {
  return apiFetch('/api/studio/social-queue');
}
export function reviewStudioSocialPost(id: string, action: 'approve' | 'reject'): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/api/studio/social-posts/${encodeURIComponent(id)}/review`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ action }) });
}

/* ---- DNN / EasyDNNNews push (legacy sites) --------------------------- */
export interface DnnConfig {
  siteDomain: string; username: string; author: string; hasPassword: boolean;
  /** Default category tags per kind — blogs get topical tags, case studies "Case Study". */
  blogCategoryIds: number[]; caseStudyCategoryIds: number[];
  blogModuleId: number; caseStudyModuleId: number;
}
/** `level`/`parentId` describe the EasyDNNNews category tree. Older module builds
 *  send neither, in which case every category reads as a root (one flat level). */
export interface DnnCategory { id: number; name: string; level: number; parentId: number | null }
/** An EasyDNNnews module instance — a site can have one per page (/blog, /case-study). */
export interface DnnModule { moduleId: number; tabId: number; moduleTitle: string; tabUrl: string }
export function getDnnConfig(): Promise<DnnConfig> {
  return apiFetch('/api/generate/dnn');
}
export function setDnnConfig(input: { siteDomain: string; username: string; password?: string; author: string; blogCategoryIds: number[]; caseStudyCategoryIds: number[]; blogModuleId?: number; caseStudyModuleId?: number }): Promise<void> {
  return apiFetch('/api/generate/dnn', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
}
export function getDnnCategories(domain?: string): Promise<{ categories: DnnCategory[] }> {
  return apiFetch(`/api/generate/dnn/categories${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`);
}
export function getDnnModules(domain?: string): Promise<{ modules: DnnModule[] }> {
  return apiFetch(`/api/generate/dnn/modules${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`);
}
/** CDN image to offer as the DNN ArticleImage, from the piece's source story. */
export function getDnnHero(input: { storyId?: string; articleId?: string; project?: string }): Promise<{ imageUrl: string; images: string[] }> {
  const q = new URLSearchParams();
  if (input.storyId) q.set('storyId', input.storyId);
  if (input.articleId) q.set('articleId', input.articleId);
  if (input.project) q.set('project', input.project);
  return apiFetch(`/api/generate/dnn/hero?${q.toString()}`);
}
export function testDnn(): Promise<{ ok: boolean; status: number; message: string }> {
  return apiFetch('/api/generate/dnn-test', { method: 'POST' });
}
/** Thrown-error marker for the duplicate-title 409 — apiFetch stringifies the
    body, so the UI matches on this to offer "push anyway". */
export const DNN_TITLE_EXISTS = 'dnn_title_exists';
/* The lead image as a URL the Triton-DNN-API module can use. The module decodes
   with System.Drawing, which reads JPEG/PNG/GIF but not WebP or HEIC — and most
   R2 photos are WebP — so anything else is re-encoded to JPEG here and that copy
   put on R2 for the module to fetch (see /dnn/image in worker/generate.ts). */
const DNN_READABLE = /\.(jpe?g|png|gif)(?:[?#]|$)/i;
async function dnnReadyImage(url: string | undefined): Promise<string | undefined> {
  const src = (url || '').trim();
  if (!src || DNN_READABLE.test(src)) return src || undefined;
  const blob = await apiBlob(`/api/generate/dnn/image-source?url=${encodeURIComponent(src)}`, true);
  /* 1920 matches what the module stores (ImageHelper.MaxImageWidth, from 1.0.6;
     780 before). Sending less caps the article's main image below what the site
     keeps; sending more is bytes the module throws away. maxDim is the longer
     side, so a portrait photo arrives narrower than this — the same as it would
     on any page. */
  const jpeg = await processImage(new File([blob], 'lead', { type: blob.type }), { mime: 'image/jpeg', maxDim: 1920, quality: 0.86 });
  if (jpeg.type !== 'image/jpeg') {
    throw new Error("The lead image couldn't be converted to JPEG in this browser, and DNN can't read it as it is. Pick a different photo, or try Chrome or Edge.");
  }
  const r = await apiFetch<{ url: string }>('/api/generate/dnn/image', {
    method: 'POST', headers: { 'content-type': 'image/jpeg' }, body: jpeg,
  });
  return r.url;
}

/* A case study's photo grid is added by the Worker from the photos on record —
   articleId for a saved piece, storyId (+ project) straight from the generator. */
export async function pushToDnn(input: { title: string; summary?: string; html: string; metaTitle?: string; metaDescription?: string; metaKeywords?: string; imageUrl?: string; draft: boolean; kind?: 'blog' | 'case_study'; categoryIds?: number[]; moduleId?: number; articleId?: string; storyId?: string; project?: string; allowDuplicate?: boolean; publishDate?: string }): Promise<{ url: string; draft: boolean; updated: boolean; articleId: number | null; previousUrl: string; note?: string }> {
  const imageUrl = await dnnReadyImage(input.imageUrl);
  return apiFetch('/api/generate/dnn-push', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...input, imageUrl }) });
}

export interface ContentMatrixClient { orgId: string; name: string; months: number[]; total: number }
export async function getContentMatrix(year?: number): Promise<{ year: number; years: string[]; clients: ContentMatrixClient[] }> {
  return apiFetch(`/api/admin/content-matrix${year ? `?year=${year}` : ''}`);
}
export interface AdminArticle {
  id: string;
  clientId: string;
  clientName: string;
  title: string;
  type: string;
  stage: ArticleStage;
  source: 'ai' | 'human';
  words: number;
  interviewee: string;
  createdAt: string;
  publishedAt: string | null;
  /** Astro or DNN delivery time, whichever happened. Empty = never pushed. */
  pushedAt: string | null;
  /** Who started it. null = created before origin was recorded. */
  origin: 'client' | 'triton' | null;
  createdByName: string | null;
  rejected: boolean;
  managerId: string | null;
  managerName: string | null;
  archived?: boolean;
  /** The interview this was generated from, if any. */
  sourceStoryId?: string;
  /** An interview covering several jobs, one still without its case study. */
  moreToWrite?: boolean;
}
export interface AdminClientStat {
  orgId: string;
  name: string;
  lastInterviewAt: string | null;
  count: number;
  managerId: string | null;
}

export function getAdminContent(): Promise<{
  articles: AdminArticle[];
  clients: AdminClientStat[];
  managers: Manager[];
}> {
  return apiFetch('/api/admin/content');
}

/* ---- what is waiting on the team (Admin → Content) ----------------------- */

export type AdminWaitingKind =
  | 'interview_review' | 'draft_review' | 'interview_abandoned'
  | 'faq_review' | 'topic_approval' | 'stale_questions' | 'expert_review' | 'unattributed' | 'published';
export interface AdminWaitingItem {
  clientId: string;
  clientName: string;
  kind: AdminWaitingKind;
  label: string;
  days: number;
  since?: string | null;
  url?: string;
  owner: { email: string; name: string } | null;
}
export function getAdminWaiting(): Promise<{ items: AdminWaitingItem[]; me: string }> {
  return apiFetch('/api/admin/waiting');
}
/** Send the interview-complete email for one article again, to whoever it routes to today. */
export function notifyArticleTeam(id: string): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/content/${encodeURIComponent(id)}/notify`, { method: 'POST' });
}
/** Queue every finished AI interview nobody wrote up for the auto-writer; dryRun only lists them. */
export function autowriteBacklog(dryRun: boolean): Promise<{ candidates: { id: string; client: string; title: string; writes: 'blog' | 'case_study' }[]; queued: number }> {
  return apiFetch('/api/admin/content/autowrite-backlog', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ dryRun }) });
}

export function getPhotoUrl(id: string): Promise<string> {
  return apiBlobUrl(`/api/content/photos/${encodeURIComponent(id)}`);
}

/* Upload a photo onto a case study.
 *
 * `img` carries the dimensions the browser already measured while downscaling
 * (processImageSized) — the gallery stores them so a site never has to fetch an
 * image to learn its size, which is unaffordable when rendering per request.
 *
 * Filing it in an album is a separate act, per photo, afterwards — see
 * addAssetToAlbum. */
export async function uploadContentPhoto(
  articleId: string,
  img: { file: File; width: number; height: number } | File,
  opts: { projectId?: string } = {},
): Promise<{ id: string; assetId: string; cdnUrl: string }> {
  const file = img instanceof File ? img : img.file;
  const q = new URLSearchParams({ filename: file.name });
  if (!(img instanceof File)) { q.set('w', String(img.width)); q.set('h', String(img.height)); }
  if (opts.projectId) q.set('project', opts.projectId);

  return apiFetch(`/api/content/${encodeURIComponent(articleId)}/photos?${q.toString()}`, {
    method: 'POST',
    headers: { 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
}

const STAGE_LABEL: Record<ArticleStage, string> = {
  0: 'Interview', 1: 'Drafting', 2: 'In review', 3: 'Published',
};

/* `pushed` corrects the delivery record by hand — for articles pushed before
   0072 started recording it, which still read as drafts. */
export async function setArticleStage(id: string, stage: ArticleStage, pushed?: boolean): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ stage, statusDate: STAGE_LABEL[stage], ...(pushed === undefined ? {} : { pushed }) }),
  });
}

/* Edit an article's body/title/summary (admin). Body is the full markdown/Astro text. */
/** AI writes the SEO title and meta description from the article. `force` replaces existing ones. */
export async function writeArticleMeta(id: string, force = false): Promise<{ metaTitle: string; metaDescription: string }> {
  return apiFetch(`/api/content/${encodeURIComponent(id)}/meta/write`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ force }),
  });
}
export async function updateArticle(id: string, patch: { title?: string; summary?: string; body?: string; expertId?: string | null; metaTitle?: string; metaDescription?: string }): Promise<void> {
  await apiFetch(`/api/content/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

/* ---- contentIQ AI interview ------------------------------------------ */

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface InterviewStyle {
  id: string;
  label: string;
  description: string;
}
export interface InterviewSettings {
  enabled: boolean;
  style: string; // account default
  styles: InterviewStyle[]; // pickable at intake
  jobTypes: string[]; // Job Type options from the client's profile (services)
}

export async function getInterviewSettings(): Promise<InterviewSettings> {
  const r = await apiFetch<{ aiInterviewEnabled: boolean; style: string; styles: InterviewStyle[]; jobTypes?: string[] }>(
    '/api/interview/settings',
  );
  return { enabled: r.aiInterviewEnabled, style: r.style, styles: r.styles ?? [], jobTypes: r.jobTypes ?? [] };
}

export async function sendInterviewMessage(
  messages: ChatTurn[],
  style?: string,
  articleId?: string,
): Promise<{ reply: string; done: boolean }> {
  return apiFetch('/api/interview/message', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages, style, articleId }),
  });
}

/* Admin: interview styles with the full effective prompt each produces. */
export interface InterviewStyleDetail extends InterviewStyle {
  prompt: string;
}
/** The service-account emails to grant on a client's GA4 property / Search
    Console site, one per slot. */
export function getGoogleAccounts(): Promise<{ accounts: { slot: '1' | '2'; email: string }[] }> {
  return apiFetch('/api/admin/google-accounts');
}

/** A client's sitemap pages for the admin SEO focus picker (any client, not the active org). */
export function getClientSitemapPages(orgId: string): Promise<{ pages: SitemapPage[] }> {
  return apiFetch(`/api/generate/sitemap-pages/${encodeURIComponent(orgId)}`);
}

/** Put one staff member in a team role on every client (Admin → Team Triton). */
export function applyTeamRoleToAll(role: TeamRoleKey, userId: string): Promise<{ ok: true; updated: number; clients: number }> {
  return apiFetch('/api/admin/team/apply-all', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ role, userId }) });
}

export function getInterviewStyles(): Promise<{ styles: InterviewStyleDetail[] }> {
  return apiFetch('/api/admin/interview-styles');
}

export async function completeInterview(
  messages: ChatTurn[],
  articleId?: string,
): Promise<{ id: string; title: string }> {
  return apiFetch('/api/interview/complete', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages, articleId }),
  });
}

/* ---- campaignIQ (live: /api/campaigns on D1, synced from Google Ads) ---- */

export interface CampaignLead {
  uid: string;
  name: string;
  score: number;
  value: number | null;
  status: string;
  receivedAt: string | null;
}
export interface Campaign {
  id: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  conversionsValue: number;
  hasAds: boolean;
  leadCount: number;
  leadValue: number;
  soldCount: number;
  leads: CampaignLead[];
}
export interface CampaignTotals {
  cost: number;
  clicks: number;
  impressions: number;
  conversions: number;
  conversionsValue: number;
}

export function getCampaigns(range: string): Promise<{
  campaigns: Campaign[];
  totals: CampaignTotals;
  paidLeads: number;
  syncedAt: string | null;
  /** False when neither Google Ads nor campaign-tagged paid leads exist: show the pitch, not an empty range. */
  configured?: boolean;
}> {
  return apiFetch(`/api/campaigns?range=${encodeURIComponent(range)}`);
}

export interface CampaignInsights {
  configured: boolean;
  empty?: boolean;
  summary?: string;
  campaigns?: { name: string; role: string; stat: string }[];
  cached?: boolean;
  generatedAt?: string;
  error?: string;
}
export function getCampaignInsights(range: string, refresh = false): Promise<CampaignInsights> {
  return apiFetch(`/api/campaigns/insights?range=${encodeURIComponent(range)}${refresh ? '&refresh=1' : ''}`);
}

/* ---- AI recommendations (live: /api/recommendations) ------------------ */

export type RecStatus = 'pending' | 'approved' | 'hidden';
export interface Recommendation {
  id: string;
  title: string;
  detail: string;
  rationale: string;
  category: string;
  impact: 'high' | 'medium' | 'low';
  campaignName: string | null;
  status: RecStatus;
  createdAt: string;
}

export async function getApprovedRecommendations(): Promise<Recommendation[]> {
  const { recommendations } = await apiFetch<{ recommendations: Recommendation[] }>('/api/recommendations');
  return recommendations;
}

export async function getAllRecommendations(): Promise<Recommendation[]> {
  const { recommendations } = await apiFetch<{ recommendations: Recommendation[] }>('/api/recommendations/all');
  return recommendations;
}

export async function analyzeRecommendations(): Promise<{ generated: number; recommendations: Recommendation[] }> {
  return apiFetch('/api/recommendations/generate', { method: 'POST' });
}

export async function setRecommendationStatus(id: string, status: RecStatus): Promise<void> {
  await apiFetch(`/api/recommendations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

/* ---- trafficIQ (live: /api/traffic on the GA4 Data API) --------------- */

export interface TrafficKpi { cur: number; prev: number }
export type CompareBasis = 'prev' | 'yoy';
export interface TrafficPayload {
  configured: boolean;
  propertyId?: string;
  fetchedAt?: string;
  cached?: boolean;
  error?: string;
  compare?: CompareBasis;
  kpis?: {
    sessions: TrafficKpi;
    engagementRate: TrafficKpi;
    conversions: TrafficKpi;
    costPerLead: { cur: number | null };
  };
  ai?: {
    sessions: number; share: number; leads: number; convRate: number; avgEngagement: number;
    sources: { name: string; sessions: number; conv: number; rate: number }[];
    hasData: boolean;
  };
  trendSessions?: number[];
  trendNewUsers?: number[];
  trendAi?: number[];
  channels?: { name: string; sessions: number; conv: number; rate: number; tone: string; isNew: boolean }[];
  events?: { key: string; name: string; count: number }[];
  cities?: { city: string; sessions: number; conv: number }[];
  pages?: { page: string; sessions: number; engSeconds: number; rate: number; conv: number }[];
  device?: { name: string; sessions: number }[];
  newReturning?: { name: string; users: number }[];
}

export function getTraffic(range: string, segment: string, compare: CompareBasis = 'prev'): Promise<TrafficPayload> {
  return apiFetch(`/api/traffic?range=${encodeURIComponent(range)}&segment=${encodeURIComponent(segment)}&compare=${compare}`);
}

export interface TrafficInsights {
  configured: boolean;
  empty?: boolean;
  summary?: string;
  sources?: { name: string; role: string; stat: string }[];
  seasonalNote?: string;
  cached?: boolean;
  generatedAt?: string;
  error?: string;
}
export function getTrafficInsights(range: string, refresh = false): Promise<TrafficInsights> {
  return apiFetch(`/api/traffic/insights?range=${encodeURIComponent(range)}${refresh ? '&refresh=1' : ''}`);
}

export interface LeadPageStat { path: string; leads: number; leadUids: string[] }
export interface LeadPagesResult { pages: LeadPageStat[]; total: number; matched: number }
export function getLeadPages(range: string): Promise<LeadPagesResult> {
  return apiFetch(`/api/traffic/lead-pages?range=${encodeURIComponent(range)}`);
}

export interface WhatChanged {
  configured: boolean;
  empty?: boolean;
  summary?: string;
  changes?: { metric: string; direction: 'up' | 'down' | 'flat'; delta: string; note: string }[];
  cached?: boolean;
  generatedAt?: string;
  error?: string;
}
export function getWhatChanged(range: string, refresh = false): Promise<WhatChanged> {
  return apiFetch(`/api/traffic/whatschanged?range=${encodeURIComponent(range)}${refresh ? '&refresh=1' : ''}`);
}

/* ---- Search Console (live: /api/search) ------------------------------ */

export interface SearchRow { key: string; clicks: number; impressions: number; ctr: number; position: number }
export interface SearchPayload {
  configured: boolean;
  siteUrl?: string;
  totals?: { clicks: number; impressions: number; ctr: number; position: number };
  pages?: SearchRow[];
  queries?: SearchRow[];
  cached?: boolean;
  fetchedAt?: string;
  error?: string;
}
export function getSearchConsole(range: string): Promise<SearchPayload> {
  return apiFetch(`/api/search?range=${encodeURIComponent(range)}`);
}

/* ---- reviewsIQ (live: /api/reviews on GatherUp) ---------------------- */

export interface ReviewItem {
  id: number;
  author: string;
  rating: number;
  date: string;
  content: string;
  response: { from: string; content: string; date: string } | null;
  location?: string;
}
export interface ReviewLocation {
  id: string;
  label: string;
  primary: boolean;
  total: number;
  average: number;
  inRange: number;
  lastReviewDate: string | null;
  staleDays: number | null;
  isStale: boolean;
  error?: string;
}
export interface ReviewsPayload {
  configured: boolean;
  total?: number;
  average?: number;
  inRange?: number;
  avgInRange?: number;
  responseRate?: number;
  ratingDist?: number[]; // [1★..5★]
  trend?: { label: string; count: number }[];
  recent?: ReviewItem[];
  lastReviewDate?: string | null;
  staleDays?: number | null;
  staleThreshold?: number;
  isStale?: boolean;
  staleLocation?: string;
  locations?: ReviewLocation[]; // only for a client with more than one review feed
  location?: string;
  truncated?: boolean;
  cached?: boolean;
  fetchedAt?: string;
  error?: string;
}
export function getReviews(range: string, location = ''): Promise<ReviewsPayload> {
  return apiFetch(`/api/reviews?range=${encodeURIComponent(range)}${location ? `&location=${encodeURIComponent(location)}` : ''}`);
}

export interface ReviewSummary {
  configured: boolean;
  empty?: boolean;
  consensus?: string;
  sentiment?: 'positive' | 'mixed' | 'negative';
  positives?: string[];
  negatives?: string[];
  reviewCount?: number;
  windowDays?: number;
  averageInWindow?: number;
  cached?: boolean;
  generatedAt?: string;
  error?: string;
}
export function getReviewSummary(range: string, refresh = false, location = ''): Promise<ReviewSummary> {
  return apiFetch(`/api/reviews/summary?range=${encodeURIComponent(range)}${refresh ? '&refresh=1' : ''}${location ? `&location=${encodeURIComponent(location)}` : ''}`);
}

/* ---- leadIQ AI insights (live: /api/leads/insights) ------------------ */

export interface LeadInsights {
  configured: boolean;
  empty?: boolean;
  count?: number;
  avgScore?: number | null;
  scored?: number;
  topSources?: { source: string; count: number; avgScore: number | null; sold: number }[];
  channels?: { channel: string; count: number }[];
  askingFor?: { summary: string; themes: { label: string; share: number }[] };
  tip?: string;
  aiCached?: boolean;
  aiGeneratedAt?: string;
  aiError?: string;
}
export function getLeadInsights(range: string, refresh = false): Promise<LeadInsights> {
  return apiFetch(`/api/leads/insights?range=${encodeURIComponent(range)}${refresh ? '&refresh=1' : ''}`);
}

/* ---- dashboard AI snapshot (live: /api/overview) --------------------- */

export interface OverviewSnapshot {
  configured: boolean;
  empty?: boolean;
  summary?: string;
  highlights?: { area: string; text: string }[];
  deliverables?: { kind: 'content' | 'reviews'; text: string }[];
  cached?: boolean;
  generatedAt?: string;
  error?: string;
}
export function getOverviewSnapshot(refresh = false): Promise<OverviewSnapshot> {
  return apiFetch(`/api/overview${refresh ? '?refresh=1' : ''}`);
}

/* ---- client-site legal pages (admin) ---------------------------------- */

/** Mirrors worker/legal-render.ts LegalConfig. Every per-site decision is a flag;
    the legal wording itself is never edited per client. */
export interface LegalConfig {
  clientLegalName: string; clientShortName: string; businessType: string; serviceArea: string;
  entityType: string; licenseNumber: string;
  email: string; phone: string; addressLines: string[]; state: string; county: string;
  effectiveDate: string; lastUpdated: string;
  callTrackingVendor: string;
  chat: boolean; chatVendor: string;
  formVendorUsed: boolean; formVendor: string;
  texting: boolean; careersPage: boolean;
  enhancedConversions: boolean; metaPixel: boolean; metaCapi: boolean; ccpaCovered: boolean;
  noSessionReplayVerified: boolean;
  retention: string; callRecordingRetention: string; chatRetention: string; analyticsRetention: string; jobApplicationRetention: string;
  aiVendorName: string;
}
export type LegalDoc = 'privacy' | 'terms' | 'notices';
export interface LegalState { config: LegalConfig; stored: boolean; updatedAt: string | null; updatedBy: string | null; errors: string[]; clientName: string }

/* ---- site-map registry (Admin → client → Content) ------------------- */
export interface SitemapParity {
  hasBlob: boolean; hasRegistry: boolean; legacyRows: number; legacyPages: number; registryPages: number;
  byStatus: Record<string, number>; origin: string; bizWebsite: string | null; stagingUrl: string | null;
  missing: string[]; extra: string[];
  anchorDiffs: { route: string; legacy: string; registry: string }[];
  hostDiffs: { route: string; legacy: string; registry: string }[];
  focusMatches: boolean; titled: number; briefed: number;
}
export interface SitemapBackfillResult {
  inserted: number; updated: number; unchanged: number; total: number; titled: number; briefed: number; focus: number;
  origin: string; originNote: string | null; legacyRows: number;
}
export function getSitemapRegistry(orgId: string): Promise<{ parity: SitemapParity }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/sitemap-registry`);
}
export function backfillSitemapRegistry(orgId: string): Promise<{ result: SitemapBackfillResult; parity: SitemapParity }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/sitemap-registry/backfill`, { method: 'POST' });
}

/* ---- Site map builder (contentIQ → Vault, active org) ----------------- */
export type SitePageStatus = 'planned' | 'scaffolded' | 'built' | 'live' | 'retired';
export interface SitePage {
  id: string; route: string; parentRoute: string | null; sort: number; title: string | null; anchors: string | null;
  pageType: string | null; status: SitePageStatus; origin: string | null; hidden: boolean; keywords: string | null; brief: string | null;
  focusRank: number | null; repoPath: string | null; lastSeenXml: string | null; lastSeenRepo: string | null; missingSince: string | null;
  url: string; createdAt: string; updatedAt: string;
}
export interface SiteMapSync {
  at: string; files: number; truncated: boolean; matched: number; scaffolded: number; built: number;
  missing: string[]; unplanned: { route: string; path: string }[]; renames: { from: string; to: string; path: string; score: number }[]; checked: number;
}
export type PageReadiness = { service: string; serviceId: string; covered: string[]; open: string[]; state: 'none' | 'thin' | 'ready' };
export interface SiteMapTree {
  pages: SitePage[]; origin: string; stagingUrl: string | null; bizWebsite: string | null; migrated: boolean; legacyRows: number;
  counts: Record<string, number>; repo: { repo: string; branch: string; pagesPath: string } | null;
  sync: SiteMapSync | null; ignored: string[]; readiness: Record<string, PageReadiness>;
  xml: { at: string; url: string; added: string[]; missing: string[]; crawled: number; excluded: number; error: string | null } | null;
}
export interface BrokenLink { fromKind: string; fromId: string; title: string; siteUrl: string | null; onSite: boolean; toRoute: string; nowRoute: string | null; reason: 'moved' | 'retired' | 'unknown' }
export interface LinkReport { broken: BrokenLink[]; total: number; indexed: number }
export interface Redirect { from: string; to: string; status: string }
export interface RedirectsPreview { path: string; current: Redirect[]; additions: Redirect[]; merged: Redirect[]; exists: boolean }
export function getSiteMapLinks(): Promise<LinkReport> { return apiFetch('/api/site-pages/links'); }
export function backfillSiteMapLinks(): Promise<{ articles: number; links: number; report: LinkReport }> { return apiFetch('/api/site-pages/links/backfill', { method: 'POST' }); }
export function getSiteMapRedirects(): Promise<RedirectsPreview> { return apiFetch('/api/site-pages/redirects'); }
export function pushSiteMapRedirects(): Promise<{ url: string; added: number }> { return apiFetch('/api/site-pages/redirects/push', { method: 'POST' }); }
export function syncSiteMapXml(): Promise<{ xml: NonNullable<SiteMapTree['xml']> }> { return apiFetch('/api/site-pages/sync-xml', { method: 'POST' }); }
export function nameSiteMapFromUrls(): Promise<{ renamed: number; seeded: number; kept: number }> { return apiFetch('/api/site-pages/names-from-urls', { method: 'POST' }); }
export interface PageQuestion { q: string; route: 'interview' | 'faq'; slot?: string; why: string }
export interface PlanGroup {
  service: string | null; pageType: string; pages: { route: string; title: string | null; status: string; url: string }[];
  covered: string[]; open: string[]; state: 'none' | 'thin' | 'ready' | 'n/a';
}
export interface SitePageInput {
  parentRoute?: string | null; slug?: string; title?: string; pageType?: string; keywords?: string; brief?: string;
  hidden?: boolean; anchors?: string; focusRank?: number | null; status?: 'planned' | 'live'; cascadeType?: boolean;
}
export interface RenamePlan {
  from: string; to: string; moves: { from: string; to: string }[];
  affected: { faqs: number; topics: number; health: number; services: number; blogTopics: number; articles: number; links: number };
}
export interface SiteMapImportPreview { added: string[]; seen: number; missing: string[]; applied: boolean; excluded?: number; total: number }
export const SITE_MAP_KEY = ['siteMap'] as const;
const postJson = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
export function getSiteMap(): Promise<SiteMapTree> { return apiFetch('/api/site-pages'); }
export function migrateSiteMap(): Promise<{ result: SitemapBackfillResult; parity: SitemapParity }> { return apiFetch('/api/site-pages/migrate', { method: 'POST' }); }
export function createSitePage(input: SitePageInput): Promise<{ page: SitePage }> { return apiFetch('/api/site-pages', postJson(input)); }
export function updateSitePage(id: string, patch: SitePageInput): Promise<{ page: SitePage }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}`, { ...postJson(patch), method: 'PUT' });
}
export function retireSitePage(id: string): Promise<{ children: number }> { return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/retire`, { method: 'POST' }); }
export function restoreSitePage(id: string): Promise<{ page: SitePage }> { return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/restore`, { method: 'POST' }); }
export function previewMoveSitePage(id: string, parentRoute: string | null): Promise<{ rename: RenamePlan | null }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/move`, postJson({ parentRoute, preview: true }));
}
export function moveSitePage(id: string, parentRoute: string | null, index: number): Promise<{ renamed: RenamePlan | null; route: string }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/move`, postJson({ parentRoute, index }));
}
export function renameSitePage(id: string, to: string, preview: boolean): Promise<{ rename: RenamePlan }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/rename`, postJson({ to, preview }));
}
export function importSiteMapXml(url: string, apply: boolean): Promise<SiteMapImportPreview> { return apiFetch('/api/site-pages/import/xml', postJson({ url, apply })); }
export function importSiteMapPaste(domain: string, text: string, apply: boolean): Promise<SiteMapImportPreview> { return apiFetch('/api/site-pages/import/paste', postJson({ domain, text, apply })); }
export const SITE_MAP_EXPORT_PATH = '/api/site-pages/export';
export function syncSiteMapRepo(): Promise<{ sync: SiteMapSync }> { return apiFetch('/api/site-pages/sync-repo', { method: 'POST' }); }
export function addRepoPageToSiteMap(route: string, path: string): Promise<{ page: SitePage; sync: SiteMapSync | null }> { return apiFetch('/api/site-pages/repo/add', postJson({ route, path })); }
export function ignoreRepoRoute(route: string, ignored: boolean): Promise<{ ignored: string[] }> { return apiFetch('/api/site-pages/repo/ignore', postJson({ route, ignored })); }
export function acceptRepoRename(from: string, to: string, path: string, preview: boolean): Promise<{ rename: RenamePlan; sync?: SiteMapSync | null }> {
  return apiFetch('/api/site-pages/repo/rename', postJson({ from, to, path, preview }));
}
export function scaffoldSiteMap(routes?: string[]): Promise<{ pushed: { route: string; path: string }[]; skipped: string[]; remaining: number }> {
  return apiFetch('/api/site-pages/scaffold', postJson({ routes }));
}
export interface LedgerQuestion {
  id: string; route: string; q: string; routeTo: 'interview' | 'faq'; slot: string | null; why: string | null;
  source: 'generic' | 'specific' | 'custom'; state: 'open' | 'sent' | 'answered' | 'removed';
  answer: string | null; answerSource: string | null; answeredAt: string | null; faqSourceId: string | null; sort: number;
  /** The Vault source a follow-up builds on. */
  buildsOn: string | null;
}
export interface PageQuestionSet {
  questions: LedgerQuestion[]; missing: string[]; coverage: { service: string; covered: string[]; open: string[] } | null; stale: boolean; checkedAt: string | null;
  result?: { checked: number; answered: number; faqsFiled: number; slotsCovered: number; audited?: boolean; followups?: number };
}
const qPath = (id: string, tail = '') => `/api/site-pages/${encodeURIComponent(id)}/questions${tail}`;
export function getSitePageQuestions(id: string): Promise<PageQuestionSet> { return apiFetch(qPath(id)); }
export function addSitePageQuestion(id: string, q: string, routeTo: 'interview' | 'faq'): Promise<PageQuestionSet> { return apiFetch(qPath(id), postJson({ q, routeTo })); }
export function removeSitePageQuestion(id: string, qid: string): Promise<PageQuestionSet> { return apiFetch(qPath(id, `/${encodeURIComponent(qid)}`), { method: 'DELETE' }); }
export function markSitePageQuestionsSent(id: string, ids: string[]): Promise<PageQuestionSet> { return apiFetch(qPath(id, '/sent'), postJson({ ids })); }
export function suggestSitePageQuestions(id: string): Promise<PageQuestionSet> { return apiFetch(qPath(id, '/suggest'), { method: 'POST' }); }
export function checkSitePageQuestions(id: string): Promise<PageQuestionSet> { return apiFetch(qPath(id, '/check'), { method: 'POST' }); }
export function clearSitePageQuestions(id: string): Promise<PageQuestionSet> { return apiFetch(qPath(id, '/clear'), { method: 'POST' }); }
export function getInterviewPlan(): Promise<{ groups: PlanGroup[] }> { return apiFetch('/api/site-pages/interview-plan'); }
/* The Write site board (docs/bulk-write-plan.md, Phase A). */
export type WriteBoardAction = 'brief' | 'questions' | 'check' | 'outline';
export interface WriteBoardRow {
  id: string; route: string; name: string; depth: number; pageType: string | null; status: string;
  keywords: string | null; brief: string | null; missing: string[]; briefSuggested: boolean; hold: boolean; ready: boolean;
  vault: { checkedAt: string | null; stale: boolean };
  questions: { open: number; sent: number; answered: number; followups: number };
  readiness: { service: string; state: 'none' | 'thin' | 'ready'; covered: number; total: number } | null;
  sectionNotes: number | null;
  faqs: { live: number; waiting: number };
  outline: { at: string | null; stale: boolean; edited: boolean; topics: number } | null;
  thin: string[];
  queued: WriteBoardAction | null;
}
export interface WriteBoard { rows: WriteBoardRow[]; written: number; faqPush: boolean; stagingUrl: string | null }
export function getWriteBoard(): Promise<WriteBoard> { return apiFetch('/api/site-pages/write-board'); }
export function queueWriteBoard(ids: string[], action: WriteBoardAction | null): Promise<{ queued: number; board: WriteBoard }> {
  return apiFetch('/api/site-pages/write-board/queue', postJson({ ids, action }));
}
export function setWriteHold(id: string, hold: boolean): Promise<{ board: WriteBoard }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/hold`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ hold }) });
}
export function suggestSitePageBrief(id: string): Promise<{ filled: string[]; page: SitePage }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/brief/suggest`, { method: 'POST' });
}
export interface RescaffoldResult {
  content: string; blocks: string[]; fold: string[]; folded: string[]; notes: string[];
  pushed: { url: string } | null; review: boolean;
}
/** Rebuild a scaffold below the fold from its outline, in-app; committed on a staging site. */
export function rescaffoldSitePage(id: string): Promise<{ result: RescaffoldResult }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/rescaffold`, { method: 'POST' });
}
export function restoreRescaffold(id: string): Promise<{ pushed: { url: string } }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/rescaffold/restore`, { method: 'POST' });
}
export function getScaffoldPrompt(id: string, rescaffold = false): Promise<{ prompt: string }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/scaffold-prompt${rescaffold ? '?rescaffold=1' : ''}`);
}
/* A page's outline (docs/bulk-write-plan.md, Phase E): topics and evidence that shape the scaffold; never copy. */
export type OutlineStrength = 'strong' | 'some' | 'thin';
export type OutlineRole = 'experience' | 'expertise' | 'trust' | 'answer';
export interface OutlineTopic { topic: string; strength: OutlineStrength; role: OutlineRole; component: string; placeholder: string; evidence: string }
export interface PageOutline {
  aboveFold: { angle: string; cta: string };
  topics: OutlineTopic[];
  closingCta: string;
  length: 'short' | 'standard' | 'long';
  emphasis: string;
}
export interface OutlineState { outline: PageOutline | null; at: string | null; edited: boolean; stale: boolean }
const olPath = (id: string, tail = '') => `/api/site-pages/${encodeURIComponent(id)}/outline${tail}`;
export function getSitePageOutline(id: string): Promise<OutlineState> { return apiFetch(olPath(id)); }
export function suggestSitePageOutline(id: string, emphasis?: string): Promise<OutlineState> { return apiFetch(olPath(id, '/suggest'), postJson({ emphasis })); }
export function saveSitePageOutline(id: string, outline: PageOutline | null): Promise<OutlineState> {
  return apiFetch(olPath(id), { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ outline }) });
}
export type ScaffoldNote = Partial<Record<'purpose' | 'count' | 'title' | 'body' | 'keyword' | 'optional' | 'topic' | 'evidence', string>>;
export interface RulesProposal {
  route: string; path: string;
  blocks: { index: number; name: string; noted: boolean; note: ScaffoldNote }[];
  rules: { path: string; existing: string | null; proposed: string };
  components: string[];
  spreadable: number;
}
export interface ScaffoldWriteResult {
  route: string; path: string; content: string;
  score: { first: number | null; final: number | null; revised: boolean };
  held: string | null; pushed: { url: string } | null; review: boolean; notes: string[];
}
export function writeScaffoldedPage(id: string, input: { expertId?: string; mode?: 'polished' | 'strict' }): Promise<{ result: ScaffoldWriteResult; board: WriteBoard }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/write`, postJson(input));
}
/* The Write site run (docs/bulk-write-plan.md, Phase C). */
export type WriteRunItemState = 'queued' | 'writing' | 'pushed' | 'held' | 'skipped' | 'failed' | 'cancelled' | 'restored';
export interface WriteRunItem {
  id: string; pageId: string | null; route: string; name: string; path: string | null; sort: number; state: WriteRunItemState; attempts: number;
  scoreFirst: number | null; scoreFinal: number | null; revised: boolean;
  scoreLive: number | null; scanError: string | null; scannedAt: string | null;
  notes: string[]; error: string | null; commitUrl: string | null; canRestore: boolean; startedAt: string | null; finishedAt: string | null;
}
export interface WriteRun {
  id: string; status: 'running' | 'paused' | 'cancelled' | 'done'; expertId: string | null; mode: 'polished' | 'strict';
  createdAt: string; finishedAt: string | null; scannedAt: string | null; counts: Record<WriteRunItemState, number>; items: WriteRunItem[];
}
export interface WritePreflight {
  canStart: boolean; blockers: string[]; toWrite: { route: string; name: string }[];
  held: number; incomplete: number; questionsOut: number; forecastThin: number; noNotes: number;
  ticks: number; minutes: number; costUsd: number | null; activeRunId: string | null;
}
export interface WriteRunState { preflight: WritePreflight; run: WriteRun | null }
export function getWriteRun(): Promise<WriteRunState> { return apiFetch('/api/site-pages/write-run'); }
export function startWriteRun(input: { expertId?: string; mode: 'polished' | 'strict' }): Promise<WriteRunState> { return apiFetch('/api/site-pages/write-run', postJson(input)); }
export function steerWriteRun(runId: string, action: 'pause' | 'resume' | 'cancel'): Promise<WriteRunState> {
  return apiFetch(`/api/site-pages/write-run/${encodeURIComponent(runId)}/${action}`, { method: 'POST' });
}
export function writeRunItemAction(itemId: string, action: 'retry' | 'restore'): Promise<WriteRunState> {
  return apiFetch(`/api/site-pages/write-run/items/${encodeURIComponent(itemId)}/${action}`, { method: 'POST' });
}
export function extractScaffoldRules(id: string): Promise<RulesProposal> { return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/rules/extract`, { method: 'POST' }); }
export function commitScaffoldRules(id: string, input: { notes: { index: number; text: string }[]; rules: string | null; spread: boolean }): Promise<{ committed: string[]; skipped: string[]; remaining: number; board: WriteBoard }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/rules/commit`, postJson(input));
}
export function crawlSitePage(id: string): Promise<{ ok: true; url: string; result: 'stored' | 'updated' }> {
  return apiFetch(`/api/site-pages/${encodeURIComponent(id)}/crawl`, { method: 'POST' });
}

export function getLegalConfig(orgId: string): Promise<LegalState> {
  return apiFetch(`/api/legal/${encodeURIComponent(orgId)}`);
}
export function saveLegalConfig(orgId: string, config: LegalConfig): Promise<{ ok: true; config: LegalConfig; errors: string[] }> {
  return apiFetch(`/api/legal/${encodeURIComponent(orgId)}`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ config }),
  });
}
/** Path for one rendered document. Fetch it with apiBlobUrl (the endpoint needs
    the Bearer token, so a plain <a href> or <iframe src> cannot reach it).
    `format: 'mdx'` returns the Astro content-collection entry (privacy/terms only). */
export function legalDocPath(orgId: string, doc: LegalDoc, opts: { fragment?: boolean; download?: boolean; format?: 'html' | 'mdx' } = {}): string {
  const q = new URLSearchParams();
  if (opts.fragment) q.set('fragment', '1');
  if (opts.download) q.set('download', '1');
  if (opts.format === 'mdx') q.set('format', 'mdx');
  const qs = q.toString();
  return `/api/legal/${encodeURIComponent(orgId)}/doc/${doc}${qs ? `?${qs}` : ''}`;
}
/** Commit privacy-policy.mdx + terms-of-use.mdx to the client's Astro repo. */
export function pushLegalPages(orgId: string): Promise<{ ok: true; repo: string; branch: string; files: { path: string; url: string }[] }> {
  return apiFetch(`/api/legal/${encodeURIComponent(orgId)}/push`, { method: 'POST' });
}

export const qk = {
  kpis: ['kpis'] as const,
  performance: ['performance'] as const,
  leadSources: ['leadSources'] as const,
  leads: ['leads'] as const,
  articles: ['articles'] as const,
  campaigns: ['campaigns'] as const,
  recommendations: ['recommendations'] as const,
  reviews: ['reviews'] as const,
  leadInsights: ['leadInsights'] as const,
  trafficInsights: ['trafficInsights'] as const,
  campaignInsights: ['campaignInsights'] as const,
  contentInsights: ['contentInsights'] as const,
  search: ['search'] as const,
  whatChanged: ['whatChanged'] as const,
  overviewSnapshot: ['overviewSnapshot'] as const,
  vault: ['vault'] as const,
};

/* ---- Ask tritonIQ ------------------------------------------------------------ */
export interface ChartSpec { type: 'line' | 'bar'; title: string; labels: string[]; series: { name: string; values: number[] }[]; unit?: 'count' | 'usd' | 'percent' | 'seconds' }
export interface AssistantHandoff { topic: string; question: string; contact: { name: string; role: string } | null }
export interface AssistantMeta { tools: { name: string; args: Record<string, unknown>; summary: string }[]; sources: string[]; chart: ChartSpec | null; handoff: AssistantHandoff | null; error?: string }
export interface AssistantMessage { id: string; role: 'user' | 'assistant'; text: string; meta: AssistantMeta | null; createdAt: string }
export interface AssistantThread { id: string; title: string; userName: string; messageCount: number; createdAt: string; updatedAt: string }
export function getAssistantThreads(): Promise<{ threads: AssistantThread[] }> { return apiFetch('/api/assistant/threads'); }
export function getAssistantThread(id: string): Promise<{ thread: AssistantThread; messages: AssistantMessage[] }> { return apiFetch(`/api/assistant/threads/${encodeURIComponent(id)}`); }
export function deleteAssistantThread(id: string): Promise<{ ok: boolean }> { return apiFetch(`/api/assistant/threads/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
/** Ask a question; `thread` is an existing id or 'new'. Events: thread, status, answer, done. */
export function askAssistant(thread: string, text: string, userName: string, onEvent: (event: string, data: unknown) => void): Promise<void> {
  return apiSse(`/api/assistant/threads/${encodeURIComponent(thread)}/messages`, { text, userName }, onEvent);
}
export function emailAssistantAnswer(thread: string, messageId: string, body: { note?: string; fromName?: string; fromEmail?: string }): Promise<{ ok: boolean; to: string }> {
  return apiFetch(`/api/assistant/threads/${encodeURIComponent(thread)}/messages/${encodeURIComponent(messageId)}/email`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
}
export function getAdminAssistantThreads(orgId: string): Promise<{ threads: (AssistantThread & { messages: AssistantMessage[] })[] }> {
  return apiFetch(`/api/admin/clients/${encodeURIComponent(orgId)}/assistant`);
}

/* ---- conversationIQ (admin) ---------------------------------------------- */
export interface ChatConfig {
  enabled: boolean; personaName: string; greeting: string; tone: 'friendly' | 'professional' | 'plain'; allowPricing: boolean;
  forbiddenTopics: string[]; customRules: string; handoffMessage: string; showPhone: boolean; allowedOrigins: string[]; accent: string;
  /** The launcher on the client's site: the round bubble, or a white pill with the label and an accent sparkle. */
  launcher: 'bubble' | 'pill'; launcherLabel: string;
  booking: { kind: 'none' | 'google_calendar'; calendarId: string; impersonate: boolean; slotMinutes: number; days: number[]; startHour: number; endHour: number; timezone: string; title: string };
}
export function testChatBooking(orgId: string): Promise<{ ok: boolean; slots?: number; sample?: string[]; error?: string }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/booking/test`, { method: 'POST' });
}
export interface ChatKnowledge { id: string; question: string; answer: string; enabled: boolean; sort: number; updated_at: string }
export interface ChatAdminPayload {
  config: ChatConfig; salesLeadEmail: string; token: string; website: string; phone: string;
  corpus: { version: string; tokens: number; sources: number; builtAt: string } | null;
  knowledge: ChatKnowledge[]; stats30d: { sessions: number; leads: number; unanswered: number };
}
export interface ChatSessionRow {
  id: string; is_test: boolean; page_url: string | null; utm_source: string | null; utm_campaign: string | null; message_count: number;
  capture: { name?: string; email?: string; phone?: string; service?: string }; lead_uid: string | null; status: string; intent: string | null;
  summary: string | null; unanswered: number; pricing_blocked: number; started_at: string; last_at: string; source: string;
}
const json = (body: unknown) => ({ headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
export function getChatAdmin(orgId: string): Promise<ChatAdminPayload> { return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}`); }
export function saveChatAdmin(orgId: string, body: { config: ChatConfig; salesLeadEmail: string }): Promise<{ ok: boolean; config: ChatConfig }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}`, { method: 'PUT', ...json(body) });
}
export function rotateChatToken(orgId: string): Promise<{ token: string }> { return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/token`, { method: 'POST' }); }
export function getChatCorpus(orgId: string, rebuild = false): Promise<{ version: string; tokens: number; sources: { id: string; kind: string; title: string; url: string | null }[]; text: string; builtAt: string; rebuilt: boolean }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/corpus${rebuild ? '?rebuild=1' : ''}`);
}
export function addChatKnowledge(orgId: string, body: { question: string; answer: string }): Promise<{ id: string }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/knowledge`, { method: 'POST', ...json(body) });
}
export function updateChatKnowledge(orgId: string, id: string, body: { question: string; answer: string; enabled: boolean }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/knowledge/${encodeURIComponent(id)}`, { method: 'PUT', ...json(body) });
}
export function deleteChatKnowledge(orgId: string, id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/knowledge/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function startChatTest(orgId: string): Promise<{ sessionId: string; greeting: string }> { return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/test/sessions`, { method: 'POST' }); }
export function sendChatTest(orgId: string, sessionId: string, text: string, onEvent: (event: string, data: unknown) => void): Promise<void> {
  return apiSse(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/test/sessions/${encodeURIComponent(sessionId)}/messages`, { text }, onEvent);
}
export function getChatSessions(orgId: string): Promise<{ sessions: ChatSessionRow[] }> { return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/sessions`); }
export function getChatSession(orgId: string, id: string): Promise<{ messages: { id: string; role: 'user' | 'assistant'; text: string; meta: unknown; createdAt: string }[] }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/sessions/${encodeURIComponent(id)}`);
}
export function getChatGaps(orgId: string): Promise<{ gaps: { question: string; n: number; last_at: string }[] }> { return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/gaps`); }

/* ---- conversationIQ (client view) ---------------------------------------- */
export interface ConversationPayload {
  configured: boolean; enabled: boolean; range: string;
  totals: { sessions: number; leads: number; leadRate: number; unanswered: number; avgMessages: number };
  byIntent: { intent: string; count: number }[];
  byDay: { day: string; sessions: number; leads: number }[];
  gaps: { question: string; count: number; lastAt: string }[];
}
export interface ConversationSession { id: string; startedAt: string; summary: string; intent: string; status: string; leadName: string | null; campaign: string | null; source: string; messages: number }
export function getConversationSessions(range: string, page: number, size = 10): Promise<{ sessions: ConversationSession[]; total: number; page: number; size: number }> {
  return apiFetch(`/api/conversation/sessions?range=${encodeURIComponent(range)}&page=${page}&size=${size}`);
}
export interface ConversationFaq { id: string; question: string; answer: string; enabled: boolean; updatedAt: string }
export function getConversationFaq(): Promise<{ faq: ConversationFaq[] }> { return apiFetch('/api/conversation/faq'); }
export function updateConversationFaq(id: string, body: { question: string; answer: string; enabled: boolean }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/conversation/faq/${encodeURIComponent(id)}`, { method: 'PUT', ...json(body) });
}
export function deleteConversationFaq(id: string): Promise<{ ok: boolean }> { return apiFetch(`/api/conversation/faq/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
export function getConversation(range: string): Promise<ConversationPayload> { return apiFetch(`/api/conversation?range=${encodeURIComponent(range)}`); }
export function answerConversationGap(body: { question: string; answer: string }): Promise<{ ok: boolean; id: string; resolved: number }> {
  return apiFetch('/api/conversation/faq', { method: 'POST', ...json(body) });
}
export function startConversationPreview(): Promise<{ sessionId: string; persona: string; greeting: string; business: string }> { return apiFetch('/api/conversation/preview/sessions', { method: 'POST' }); }
export function sendConversationPreview(sessionId: string, text: string, onEvent: (event: string, data: unknown) => void): Promise<void> {
  return apiSse(`/api/conversation/preview/sessions/${encodeURIComponent(sessionId)}/messages`, { text }, onEvent);
}
export function answerChatGap(orgId: string, body: { question: string; answer: string }): Promise<{ ok: boolean; id: string; resolved: number }> {
  return apiFetch(`/api/chat-admin/clients/${encodeURIComponent(orgId)}/gaps/answer`, { method: 'POST', ...json(body) });
}

/* ---- Referral Rewards ------------------------------------------------------ */
export interface ReferralDto {
  id: string; business: string; contactName: string; phone: string; email: string; website: string; notes: string;
  status: 'received' | 'contacted' | 'qualified' | 'not_fit' | 'existing_client'; statusLabel: string; statusNote: string; statusAt: string | null;
  submittedBy: string; submittedAt: string; creditAmount: number | null; creditIssuedOn: string | null; creditIssuedNote: string;
  clientId: string; clientName: string; leadUid: string | null;
}
export interface ReferralTotals { sent: number; qualified: number; earned: number; issued: number; pending: number }
export function getReferrals(): Promise<{ rate: number; referrals: ReferralDto[]; totals: ReferralTotals }> { return apiFetch('/api/referrals'); }
export function submitReferral(body: { business: string; contactName: string; phone: string; email: string; website: string; notes: string; consent: boolean }): Promise<{ ok: boolean; id: string }> {
  return apiFetch('/api/referrals', { method: 'POST', ...json(body) });
}
export interface ReferralConfig { defaultAmount: number; notify: string[]; targetOrg: string; overrides: { orgId: string; clientName: string; amount: number }[] }
export function getReferralsAdmin(): Promise<{ referrals: ReferralDto[]; config: ReferralConfig }> { return apiFetch('/api/referrals-admin'); }
export function setReferralStatus(id: string, status: ReferralDto['status'], note?: string): Promise<{ ok: boolean; creditAmount: number | null }> {
  return apiFetch(`/api/referrals-admin/${encodeURIComponent(id)}/status`, { method: 'PUT', ...json({ status, note }) });
}
export function setReferralCreditIssued(id: string, date: string | null, note?: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/referrals-admin/${encodeURIComponent(id)}/credit-issued`, { method: 'PUT', ...json({ date, note }) });
}
export function saveReferralConfig(body: { defaultAmount?: number; notify?: string[] }): Promise<{ ok: boolean }> { return apiFetch('/api/referrals-admin/config', { method: 'PUT', ...json(body) }); }
export function getReferralOverride(orgId: string): Promise<{ defaultAmount: number; override: number | null; rate: number }> {
  return apiFetch(`/api/referrals-admin/config/override/${encodeURIComponent(orgId)}`);
}
export function setReferralOverride(orgId: string, amount: number | null): Promise<{ ok: boolean }> {
  return apiFetch(`/api/referrals-admin/config/override/${encodeURIComponent(orgId)}`, { method: 'PUT', ...json({ amount }) });
}

/* ---- Reviews widget (admin) -------------------------------------------- */

export interface ReviewsWidgetClient {
  orgId: string; name: string;
  locations: { feedId: string; label: string; primary: boolean }[];
  agentId: number | null; agentName: string | null; agentMissing: boolean; duplicateAgent: boolean;
  enabled: boolean; minRating: number;
  suggestion: { agentId: number; reason: 'business' | 'name'; agentName: string } | null;
  ambiguous: number[];
  coverage: { missingFeeds: string[]; extraBusinesses: { id: string; name: string; city: string }[] } | null;
  /** Why this client can't be switched on yet. Empty = ready. */
  blockers: string[];
  sync: { lastOkAt: string | null; lastAttemptAt: string | null; lastError: string | null; lastPushedAt: string | null; lastPushedUrl: string | null; reviewCount: number | null };
}
/** src/data/reviews.json as the sync would write it (worker/reviews-widget-file.ts). */
export interface ReviewsWidgetFile {
  version: number; updated: string; minRating: number;
  business: { name: string; url: string };
  sources: Record<string, { label: string; logo: string }>;
  all: ReviewsWidgetScope;
  locations: Record<string, ReviewsWidgetScope & { label: string; primary: boolean }>;
  reviews: { id: string; location: string; source: string; author: string; rating: number; date: string; dateLabel: string; text: string; reply?: { from: string; text: string; date: string } }[];
}
export interface ReviewsWidgetScope { rating: number; count: number; links: { read?: string; write?: string; profiles: { source: string; url: string }[] } }
export interface ReviewsWidgetAdmin {
  configured: boolean; scannedAt: string | null;
  agents: { agentId: number; name: string; businessCount: number }[];
  clients: ReviewsWidgetClient[];
}
export function getReviewsWidgetAdmin(): Promise<ReviewsWidgetAdmin> { return apiFetch('/api/reviews-widget-admin'); }
export function scanGatherUp(): Promise<{ ok: boolean; agents: number; scanned: number; failed: { agentId: number; name: string; error: string }[] }> {
  return apiFetch('/api/reviews-widget-admin/scan', { method: 'POST' });
}
export function previewReviewsWidget(orgId: string): Promise<ReviewsWidgetFile> {
  return apiFetch(`/api/reviews-widget-admin/clients/${encodeURIComponent(orgId)}/preview`);
}
export function syncReviewsWidgetNow(orgId: string): Promise<{ ok: boolean; pushed: boolean; unchanged?: boolean; url?: string; reviews?: number; error?: string }> {
  return apiFetch(`/api/reviews-widget-admin/clients/${encodeURIComponent(orgId)}/sync`, { method: 'POST' });
}
export function saveReviewsWidgetClient(orgId: string, body: { agentId?: number | null; minRating?: number; enabled?: boolean }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/reviews-widget-admin/clients/${encodeURIComponent(orgId)}`, { method: 'PUT', ...json(body) });
}
export function acceptReviewsWidgetSuggestions(): Promise<{ ok: boolean; applied: number }> {
  return apiFetch('/api/reviews-widget-admin/accept-suggestions', { method: 'POST' });
}

/* ---- Page score (admin spike) ------------------------------------------ */

export interface PageScoreResult {
  /** Whether it joined the health list. False for pasted copy with no route —
      there is nothing to key it to, so it stays a one-off reading. */
  saved: boolean;
  page: { route: string; title: string; chars: number; from: 'crawl' | 'pasted' };
  effort: {
    scores: { effort: number; originality: number; skill: number; accuracy: number; overall: number };
    reasons: { effort: string; originality: string; skill: string; accuracy: string };
    generic: string[];
  } | null;
  rank: number | null;
  accuracyConcern: boolean;
  topic: { inferred: string; target: string };
  facts: PageFacts;
  proof: {
    photos: { onPage: number; stock: number; available: number };
    reviews: { quoted: number; available: number; matching: number; terms: string[] };
  };
  coverage: {
    missing: { what: string; why: string; source: { id: string; kind: string; title: string } | null }[];
    conflicts: { what: string; a: ConflictSide; b: ConflictSide }[];
    unanswered: { question: string; route: 'faq' | 'interview' | 'live' }[];
  };
  sourcesUsed: number;
  faqsOnPage: number;
}

export type ConflictSide = { id: string; kind: string; title: string } | null;

export interface PageFacts {
  title: string;
  titleLength: number;
  metaDescription: string;
  canonical: string;
  canonicalOffHost: boolean;
  h1: string[];
  h2: string[];
  questionHeadings: number;
  schemaTypes: string[];
  hasFaqSchema: boolean;
  hasPersonSchema: boolean;
  hasLocalBusinessSchema: boolean;
  hasBreadcrumbSchema: boolean;
  byline: boolean;
  datePublished: string;
  dateModified: string;
  images: { total: number; withAlt: number; own: number; stock: number; other: number };
  links: { internal: number; external: number; externalHosts: string[]; authority: number };
  wordCount: number;
  numbers: number;
  numbersPer1k: number;
  trust: { licensed: boolean; insured: boolean; bonded: boolean; warranty: boolean; certifications: string[] };
  keyword: { target: string; inTitle: boolean; inH1: boolean; inFirst100: boolean; inAnyH2: boolean } | null;
}

export function scorePage(body: { url?: string; text?: string; route?: string }): Promise<PageScoreResult> {
  return apiFetch('/api/page-health/score', { method: 'POST', ...json(body) });
}

/* ---- Client experts ----------------------------------------------------- */

export type ExpertSocialPlatform = 'linkedin' | 'instagram' | 'x' | 'facebook' | 'youtube';
export interface ExpertSocial { platform: ExpertSocialPlatform; url: string }

export interface Expert {
  id: string; name: string; title: string;
  /** One licence, certification or award per item. */
  credentials: string[];
  years: number | null; bio: string; does: string; photoAsset: string; photoUrl: string;
  socials: ExpertSocial[];
  authorPageUrl: string; authorPagePushedAt: string;
  slug: string; clerkUserId: string; status: string;
  /** draft | awaiting_review | approved — gates publication, not attribution. */
  state: 'draft' | 'awaiting_review' | 'approved'; sort: number;
}

export type ExpertDraft = Omit<Expert, 'id' | 'slug' | 'status' | 'state' | 'photoUrl' | 'authorPageUrl' | 'authorPagePushedAt'>;

/** Write the person's author page to the client's Astro repo. A 409 `exists`
    means a page tritonIQ didn't write is already there; resend with replace. */
export function pushExpertPage(id: string, replace = false): Promise<{ url: string; path: string; route: string }> {
  return apiFetch(`/api/experts/${encodeURIComponent(id)}/push`, { method: 'POST', ...json({ replace }) });
}

/** The active team as the JSON file a DNN site's TritonIQ Experts module imports. */
/** The DNN site's experts sync URL, and how its last call went ('ok' or why not). */
export interface ExpertsHook { url: string; firedAt: string; status: string }
export function getExpertsHook(): Promise<ExpertsHook> { return apiFetch('/api/experts/hook'); }
export function setExpertsHook(url: string): Promise<ExpertsHook> {
  return apiFetch('/api/experts/hook', { method: 'PUT', ...json({ url }) });
}
/** Tell the DNN site to sync now — what every save does on its own. */
export function fireExpertsHook(): Promise<ExpertsHook> {
  return apiFetch('/api/experts/hook/fire', { method: 'POST', ...json({}) });
}

export function exportExperts(): Promise<unknown> {
  return apiFetch('/api/experts/export');
}

/** Upload a headshot (already run through processImageSized). It becomes one of
    the client's gallery assets; the person points at it once the form is saved. */
export function uploadExpertPhoto(img: { file: File; width: number; height: number }): Promise<{ assetId: string; url: string }> {
  const q = new URLSearchParams({ filename: img.file.name });
  if (img.width) { q.set('w', String(img.width)); q.set('h', String(img.height)); }
  return apiFetch(`/api/experts/photo?${q.toString()}`, {
    method: 'POST',
    headers: { 'content-type': img.file.type || 'application/octet-stream' },
    body: img.file,
  });
}

export function getExperts(): Promise<{ experts: Expert[] }> { return apiFetch('/api/experts'); }
export function createExpert(body: Partial<ExpertDraft>): Promise<{ id: string; slug: string }> {
  return apiFetch('/api/experts', { method: 'POST', ...json(body) });
}
export function updateExpert(id: string, body: Partial<ExpertDraft>): Promise<{ ok: boolean }> {
  return apiFetch(`/api/experts/${encodeURIComponent(id)}`, { method: 'PUT', ...json(body) });
}
export function archiveExpert(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/experts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Move a profile through review. Separate from the edit so approving cannot
    overwrite a field somebody changed while it sat in the queue. */
export function setExpertState(id: string, state: Expert['state']): Promise<{ ok: boolean; state: string }> {
  return apiFetch(`/api/experts/${encodeURIComponent(id)}/state`, { method: 'PUT', ...json({ state }) });
}

export interface ExpertLogin { userId: string; name: string; email: string; linkedTo: string }

/** The client's own people, for linking a profile to a login. Triton staff are
    filtered out server-side — they are not the client's experts. */
export function getExpertLogins(): Promise<{ logins: ExpertLogin[]; error?: string }> {
  return apiFetch('/api/experts/logins');
}

/* ---- Services and the coverage ledger ----------------------------------- */

export type CoverageState = 'covered' | 'generic' | 'open';
export interface CoverageSlot {
  key: string; label: string; state: CoverageState; note: string; sourceId: string;
  /** Set by a person; the audit leaves it alone until released. */
  pinned: boolean;
  /** The slot as a question for this service, and where its answer goes. */
  q: string; routeTo: 'interview' | 'faq';
}
export interface ClientService {
  id: string; name: string; slug: string; routes: string[]; sort: number; status: string;
  slots: CoverageSlot[]; covered: number;
  /** When the ledger was last read against the Vault; empty means never. */
  checkedAt: string;
  /** The Vault has moved since; the sweep re-audits within minutes. */
  stale: boolean;
  pages: { route: string; title: string | null; status: string | null; onSiteMap: boolean }[];
}
export interface ServiceProposal { name: string; routes: string[]; why: string; existing: boolean }
export interface UnclaimedPage { route: string; title: string | null; status: string }

export function getServices(): Promise<{
  spine: { key: string; label: string }[]; crawledRoutes: string[]; services: ClientService[];
  registry: boolean; unclaimed: UnclaimedPage[];
}> {
  return apiFetch('/api/services');
}
/** Hand a hand-set slot back to the audit. */
export function releaseServiceSlot(id: string, slot: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/services/${encodeURIComponent(id)}/slot`, { method: 'PUT', ...json({ slot, release: true }) });
}
/** Record on the page's ledger that these slots went to the client (after the send itself). */
export function recordServiceAsked(id: string, slots: string[], how: 'faq' | 'interview'): Promise<{ ok: boolean; route: string; recorded: number }> {
  return apiFetch(`/api/services/${encodeURIComponent(id)}/asked`, { method: 'POST', ...json({ slots, how }) });
}
export function proposeServices(): Promise<{ pagesRead: number; proposals: ServiceProposal[] }> {
  return apiFetch('/api/services/propose', { method: 'POST' });
}
export function createService(body: { name: string; routes?: string[] }): Promise<{ id: string; slug: string }> {
  return apiFetch('/api/services', { method: 'POST', ...json(body) });
}
export function updateService(id: string, body: { name: string; routes?: string[]; sort?: number }): Promise<{ ok: boolean }> {
  return apiFetch(`/api/services/${encodeURIComponent(id)}`, { method: 'PUT', ...json(body) });
}
export function archiveService(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/services/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function setServiceSlot(id: string, slot: string, state: CoverageState, note = ''): Promise<{ ok: boolean }> {
  return apiFetch(`/api/services/${encodeURIComponent(id)}/slot`, { method: 'PUT', ...json({ slot, state, note }) });
}

/** Read the Vault against the fourteen slots for one service. One model call. */
export function checkServiceCoverage(id: string): Promise<{ ok: boolean; sourcesRead: number; covered: number; generic: number }> {
  return apiFetch(`/api/services/${encodeURIComponent(id)}/coverage`, { method: 'POST' });
}

/* ---- Page health: the site scan ----------------------------------------- */

export interface PageHealthRow {
  route: string; url: string; title: string; source: string;
  rank: number | null; scoredAt: string; stale: boolean;
  missing: number; unanswered: number; conflicts: number; topic: string;
  /** From the site map: the page's own name (the crawled title stays in `title`), its depth, type, status and keywords. */
  name: string; depth: number; pageType: string | null; status: string | null; keywords: string;
  readiness: PageReadiness | null;
  /** False for a page that is planned or scaffolded: nothing to score yet. */
  written: boolean;
}
/** A page the scan knows that the site map does not. */
export type PageHealthOrphan = Omit<PageHealthRow, 'name' | 'depth' | 'pageType' | 'status' | 'keywords' | 'readiness' | 'written'>;
export interface ScanStep {
  done: { route: string; rank: number | null; skipped?: boolean; error?: string }[];
  remaining: number;
  total: number;
}

export interface SiteConflict { key: string; what: string; between: string[]; routes: string[] }

export function getPageHealth(): Promise<{ conflicts: SiteConflict[]; registry: boolean; pages: PageHealthRow[]; orphans: PageHealthOrphan[]; skipped: PageHealthOrphan[] }> {
  return apiFetch('/api/page-health/pages');
}
/** Put a page the scan found onto the site map, as live. */
export function adoptSitePage(route: string, title?: string): Promise<{ page: SitePage }> {
  return apiFetch('/api/site-pages/adopt', { method: 'POST', ...json({ route, title }) });
}
/** Take a page out of the worklist for good, or put it back. */
export function setPageSkipped(route: string, skip: boolean): Promise<{ ok: boolean }> {
  return apiFetch('/api/page-health/skip', { method: 'PUT', ...json({ route, skip }) });
}
/** One batch. The caller loops until `remaining` is 0, so progress is visible
    and a run can be stopped. */
export function scanSite(limit = 3): Promise<ScanStep> {
  return apiFetch('/api/page-health/scan', { method: 'POST', ...json({ limit }) });
}
/** Re-read one page now — after answers land, to see whether the gap closed. */
export function rescanPage(route: string): Promise<ScanStep> {
  return apiFetch('/api/page-health/scan', { method: 'POST', ...json({ routes: [route] }) });
}

/* ---- Page detail and the rewriter --------------------------------------- */

export interface PageFinding { what: string; why: string; source: { id: string; kind: string; title: string } | null }
export interface PageDetail {
  route: string; url: string; title: string; source: string;
  rank: number | null; scoredAt: string; stale: boolean;
  effort: { scores: Record<string, number>; reasons: Record<string, string>; topic: string; generic: string[] } | null;
  facts: PageFacts | null;
  missing?: PageFinding[];
  unanswered?: { question: string; route: 'faq' | 'interview' | 'live' }[];
  conflicts?: { what: string; a: ConflictSide; b: ConflictSide }[];
  proof?: PageScoreResult['proof'];
  /** Question sets already sent to the client about THIS page, and how much
      has come back. Without it somebody re-asks what was answered weeks ago. */
  asked?: { sentAt: string; status: string; total: number; answered: number; declined: number }[];
  interviews?: { id: string; title: string; status: string; createdAt: string }[];
  /** From the site map, when the page is on it. `source: 'planned'` with no
      scores is a page that is not written yet. */
  name?: string; pageType?: string | null; status?: string; keywords?: string; brief?: string;
  readiness?: PageReadiness | null;
  questions?: { q: string; routeTo: string; state: string }[];
}
export interface PlacedAddition { heading: string; body: string; where: string; source: string }
export interface RewriteChange { kind: 'added' | 'extended' | 'replaced' | 'removed' | 'unchanged'; before?: string; after?: string }
export interface RewriteResult {
  mode: 'notes' | 'file';
  additions?: PlacedAddition[];
  content?: string;
  changes?: RewriteChange[];
  summary?: { added: number; extended: number; replaced: number; removed: number; unchanged: number; needsReview: RewriteChange[] };
  truncated?: boolean;
}

export function getPageDetail(route: string): Promise<PageDetail> {
  return apiFetch(`/api/page-health/page?page=${encodeURIComponent(route)}`);
}
export function rewritePage(body: {
  page: string; mode: 'notes' | 'file'; body?: string; additions: { what: string; source: string }[];
}): Promise<RewriteResult> {
  return apiFetch('/api/page-health/rewrite', { method: 'POST', ...json(body) });
}

/** Raise an interview topic from a page's unanswered questions. Created as
    'suggested' — the admin clicking it has read them, which is the approval. */
export function requestPageInterview(body: {
  page: string; title?: string;
  /* Each question carries where its answer is destined. The ones marked 'faq'
     become page FAQs, drafted from the transcript once the interview is done —
     otherwise folding them in would leave the answers stuck in a transcript. */
  questions: { q: string; route: 'faq' | 'interview' | 'live' }[];
}): Promise<{ id: string; title: string }> {
  return apiFetch('/api/topics/from-page', { method: 'POST', ...json(body) });
}

/** Tag this client's reviews with which service each is about. A batch at a
    time; the caller loops while `remaining` is non-zero. */
export interface ReviewProblem {
  /** The customer's own words for what was wrong before they called. */
  problem: string;
  /** Service names it belongs to; empty when the review never said. */
  services: string[];
  /** The pages those services claim — somewhere to actually put it. */
  routes: string[];
}
export function classifyReviews(): Promise<{
  classified: number; tagged: number; remaining: number; note?: string; problems: ReviewProblem[];
}> {
  return apiFetch('/api/services/classify-reviews', { method: 'POST' });
}
